// src/lib/transferLoads.js
// Loads — the movement half of an inter-branch transfer. Issue #517,
// Session 79.10, built against gate revision 2:
// docs/gates/Stede_PartialShipment_Gate_517.html — decisions D48–D68, approved.
//
// ── What this is, in plain words ──
//
// Dallas asks Houston for four hundred frames. Houston has one trailer, so two
// hundred and fifty go on Wednesday and the rest the week after. Before this,
// Stede could only record a transfer leaving ONCE: the first truck closed the
// whole request as sent, and the other hundred and fifty existed nowhere in the
// application at all. That is the defect #517 was logged for.
//
// A LOAD is one movement of stated quantities from one place to one place
// (D61). Houston → Austin with 250 is a load. Austin → Dallas with 50 is
// another load of the same request. Austin → Dallas with 150 next week is a
// third. They are numbered 7-1, 7-2, 7-3, so a driver can say "seven dash two"
// down a phone (D50).
//
// ── Why this is its own file rather than more of branchTransfers.js ──
//
// That file owns the DOCUMENT — asking, agreeing, refusing. This one owns the
// MOVEMENT. The database is split the same way: `branch_transfers` and its
// lines against `branch_transfer_loads` and its lines. Locked rule 14, and the
// precedent is branchTransferEmails.js, split out at 79.7 for the same reason.
//
// ── The rule to understand before changing anything here ──
//
// "PARTIALLY SHIPPED" IS NEVER STORED. It is worked out from the loads, every
// time (D49). The transfer keeps its four stages — requested, approved,
// in_transit, received — because #391's D5 fixes the requester's tracker at
// four stages for every row, and a tracker whose shape changes per row is a
// diagram rather than a progress bar. Adding a fifth status is the tempting,
// wrong answer, and it would silently drop transfers off My requests.

import { supabase } from './supabase';
import { canSeeInventoryCost } from './permissions';
import { sendNotification, getRequestApproverIds } from './notify';
import { transferLabel, makeProgress, agreedLines, isOwnShelf, receivedAverageCost, promisedDateFor, askingApproverIds } from './branchTransfers';
import { dateList } from './format';
import { cutOff } from './readLimit';
import { notifyTransferLoadArrived } from './branchTransferEmails';
import { reasonText, qtyText, extraNoSentence, extraSettleSentence, notifyTruckQuestions } from './corrections';

// ** THERE ARE NOW THREE STATES, NOT TWO — #553 D17, Session 79.15f. **
//
// This comment used to say "a load is on the road or it has been counted. There
// is no third state: an arrived load has been counted by somebody." THAT WAS
// TRUE AND IS NOW FALSE, and it is corrected here rather than left because the
// old sentence teaches the exact wrong thing to whoever reads it next.
//
//   on the road   status in_transit
//   here          status arrived, counted_at NULL — the truck is in the yard
//                 and nobody has opened the boxes. NOTHING IS ON A SHELF.
//   counted in    status arrived, counted_at set — this is when stock lands
//
// THE STATUS COLUMN HAD TWO VALUES then; the third state is the pair of
// columns read together, which is why `counted_at` and not `status` is what
// the count-in queue filters on. `transfer_load_arrival_consistent` still ties
// `arrived` to an arrival time — it never said anything about counting, though
// its own comment in 20260911 claimed it did.
//
// ** A THIRD STATUS VALUE SINCE #694, Session 79.22b.4.6.1: `undone`. ** A truck
// the person who sent it took back, with who and when (`undone_by`,
// `undone_at`). It never left, so it is on NO list of trucks and in NO
// "standing here" figure — every reader asks for `in_transit` or `arrived` by
// name, and standingAt() leaves it out explicitly. It is kept, never deleted:
// the transfer's history shows it, and the next load number still counts it.
export const LOAD_STATUSES = ['in_transit', 'arrived', 'undone'];

// The two endings, and they are deliberately different words — D56, and Colby
// confirmed these are "a regular part of every day business" rather than
// exceptional.
//
//   closed_short    — goods LEFT and never arrived. Real money gone, both
//                     branches' managers told.
//   closed_unfilled — a balance nobody is going to send. NOTHING LEFT THE
//                     BUILDING, so there is no loss to report.
//
// Calling both "short close" would put a loss report in front of a manager for
// a truck that was never loaded.
export const UNFILLED_STATUS = 'closed_unfilled';

// ─────────────────────────────────────────────────────────────────────────────
// PURE LOGIC — no database, no React. Everything below this line is testable
// without a network, and it is where every gate decision that can be got wrong
// silently actually lives.
// ─────────────────────────────────────────────────────────────────────────────

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// D50, approved by Colby, and D61 extends it: the counter just keeps going.
//
// The shape is the one the app already uses — `work_order_ref` is constrained
// to ^\d{6}(-\d+)?$, a number with an optional dash suffix — so nobody has to
// learn a new convention, and it is sayable out loud in a yard.
//
// Mirrors the database's own construction in load_branch_transfer(), including
// the fallback for a transfer that somehow has no number yet.
export function loadRef(transferNumber, seq) {
  const base = transferNumber || null;
  return `${base || 'TRF'}-${seq}`;
}

// #665 D12, Session 79.22b.4.5 -- THE NEXT LOAD'S NUMBER, BY THE DATABASE'S
// OWN RULE: load_branch_transfer() takes COALESCE(MAX(load_seq), 0) + 1. The
// load sheet counted its loads instead, which gives the same answer only while
// no number is ever skipped; one rule for the sheet and the card, and it is
// the database's. `transferId` picks one transfer's loads out of a list that
// holds several -- the To load card reads the page's open-loads list.
// The count is kept as a floor: every real load carries a distinct positive
// load_seq, so the highest is never below the count and this IS the database's
// answer; a row read without its load_seq still never offers a number already
// on a truck. An UNDONE truck counts too (#694): its number is taken, and the
// database never issues it again, so neither may the preview.
export function nextLoadSeq(loads, transferId = null) {
  const mine = (Array.isArray(loads) ? loads : [])
    .filter(l => l && (transferId == null || String(l.transfer_id) === String(transferId)));
  const highest = mine.reduce((max, l) => Math.max(max, Number(l.load_seq) || 0), 0);
  return Math.max(highest, mine.length) + 1;
}

// #665 D9, Session 79.22b.4.5 -- WHERE A LOAD GOES BY DEFAULT, ONE RULE FOR
// THE SHEET AND THE CARD. Colby, at gate 665's open: "95%+ of the transfers
// coming from houston will land at the austin hub." So the default is the
// first location switched on as a hub that is NOT one of the transfer's two
// ends: a load from Houston to Austin already ends at Austin, and dropping it
// "at the hub" would be dropping it where it is going. No hub, or only an end
// that is one: null, meaning straight through with no choice to make. Two hubs
// switched on: the first found, exactly as the sheet has always done (#691
// half 2 owns per-route choices).
export function defaultRoute(transfer, hubs) {
  return (Array.isArray(hubs) ? hubs : []).find(h => h
    && String(h.id) !== String(transfer?.from_branch_id)
    && String(h.id) !== String(transfer?.to_branch_id)) || null;
}

// #690, gate 665 D11, Session 79.22b.4.5 -- WHO IS RECORDED AS THE DRIVER,
// ONE RULE FOR THE SHEET AND THE CARD.
//
// "Who loads your trucks" was saved and changed nothing: the sheet named
// whoever pressed Send either way. Now it decides the default:
//   Drivers load their own trucks -- the person pressing, when they are on
//     the branch's driver list; otherwise nobody (a wrong name in the history
//     is worse than none, #553 B6).
//   Loaders load, drivers drive -- NOBODY, ever, at loading. The driver is
//     named at the far end, when it is collected or counted in (553 D13).
// Returns the picker's value: a user id, or '' for nobody.
export const LOADERS_LOAD = 'loader_and_driver';
export function loaderNamesNobody(loaderModel) {
  return loaderModel === LOADERS_LOAD;
}
// #699, gate 665 D28 -- "on the branch's driver list" now means the presser
// DRIVES there once anybody at the branch is switched on: presserDriver().
export function driverDefault({ currentUserId, drivers, loaderModel } = {}) {
  if (loaderNamesNobody(loaderModel) || currentUserId == null) return '';
  return presserDriver(drivers, currentUserId);
}

// WHAT IS PHYSICALLY STANDING AT A PLACE, for one item.
//
// This is the Austin case and it is the reason a load has a FROM branch at all.
// Goods dropped at Austin are off Houston's shelf and on nobody's — they belong
// to no branch's stock — so the only record of them is the loads themselves:
// what has arrived here, minus what has already been taken away from here.
//
// MIRRORS load_branch_transfer()'s own arithmetic exactly. It is duplicated
// here on purpose and for the same reason workOrderRef.js duplicates a CHECK
// constraint: so a screen can refuse an impossible number before the database
// does, with a sentence a person can act on rather than a raw exception.
export function standingAt(loads, itemId, branchId) {
  if (!Array.isArray(loads) || itemId == null || branchId == null) return 0;
  const key = String(itemId);
  let here = 0;
  for (const load of loads) {
    // #694 -- a truck taken back never left, so it takes nothing away from
    // here: load_branch_transfer() and hand_over_load() leave it out too.
    if (load?.status === 'undone') continue;
    const lines = load?.branch_transfer_load_items || load?.items || [];
    const line = lines.find(l => String(l.item_id) === key);
    if (!line) continue;
    // Arrived here. Only a counted load has landed; one still on the road has
    // not, and its qty_arrived is null.
    if (String(load.to_branch_id) === String(branchId) && load.status === 'arrived') {
      here += num(line.qty_arrived);
    }
    // Left here, whether or not it has got where it is going yet.
    if (String(load.from_branch_id) === String(branchId)) {
      here -= num(line.qty_loaded);
    }
  }
  return here > 0 ? here : 0;
}

// HOW MANY OF THIS ITEM MAY GO ON THIS LOAD — the ceiling the driver's screen
// must not let anybody past.
//
// From the ORIGIN it is whatever has been MADE and not yet sent. From anywhere
// else it is whatever is standing there. Two different questions, and
// conflating them is how somebody at Austin ships a hundred and fifty frames
// that are still at Houston.
//
// #553 D4, Session 79.15e — THIS USED TO READ `qty_requested - qty_shipped`,
// which is what let Houston put 100 frames on a truck having manufactured none.
// Asking for something is not the same as having built it, and nothing in the
// old expression could tell the difference because no number counted the
// making. It reads `qty_ready` now.
//
// THE SCREEN IS NOT THE ENFORCEMENT AND MUST NOT BE MISTAKEN FOR IT. Colby's
// rule — "work belonging on tb shouldnt be able to be completed from another
// tab" — is a CHECK constraint, `qty_shipped <= qty_ready`, so a caller that
// skips this function entirely is still refused by Postgres. What this does is
// show the ceiling BEFORE somebody hits it, which is the difference between a
// screen that guides and one that only says no.
// #628 half 2, Session 79.22b -- `fromShelf`: a made-to-order line filled from
// the sending branch's shelf may take up to what is still OWED, not only what
// is made. It is the rule load_branch_transfer() carries since
// 20260921_transfer_shelf_route_628.sql, mirrored so the screen refuses the
// same numbers the database does. A refused item was never coming.
export function loadableQty({ line, loads, itemId, fromBranchId, originBranchId, fromShelf = false }) {
  if (String(fromBranchId) === String(originBranchId)) {
    if (fromShelf) {
      if (line?.line_status === 'refused') return 0;
      const owed = num(line?.qty_requested) - num(line?.qty_shipped);
      return owed > 0 ? owed : 0;
    }
    const ready = num(line?.qty_ready);
    const shipped = num(line?.qty_shipped);
    const left = ready - shipped;
    return left > 0 ? left : 0;
  }
  return standingAt(loads, itemId, fromBranchId);
}

// One line's position, in the four numbers a person actually asks about.
//
// `outstanding` is what has NOT reached the destination — which is the number
// the requester cares about and the number the old code got wrong. `inTransit`
// is what has left but not landed.
export function lineProgress(line) {
  const requested = num(line?.qty_requested);
  // #553 D1, Session 79.15e. The fourth number: how many are BUILT and standing
  // at the supplying branch. `toMake` and `readyToLoad` are what the two tabs
  // read, and a line can carry both at once — 60 still to make, 40 ready — which
  // is correct rather than a contradiction.
  const ready = num(line?.qty_ready);
  const shipped = num(line?.qty_shipped);
  const received = num(line?.qty_received);
  const outstanding = Math.max(requested - received, 0);
  const inTransit = Math.max(shipped - received, 0);
  // #713, 682 D11, Session 79.22b.6c.3 -- how many were MADE, from the line's
  // production runs (withMadeCounts() below), never `qty_ready`, which since
  // #628 also counts goods sent off the shelf. Null where the runs were not
  // read: the words then read as before.
  const made = line?.made_by_runs == null ? null : num(line.made_by_runs);
  return {
    requested, ready, shipped, received, outstanding, inTransit, made,
    toMake: Math.max(requested - ready, 0),
    readyToLoad: Math.max(ready - shipped, 0),
    complete: received >= requested && requested > 0,
  };
}

// #665, gate 665 D1-D3, Session 79.22b.4.4 -- WHAT EACH ITEM ON A CARD SAYS.
//
// Every card names every item it carries, with the "x of y" that the tab's own
// job moves: a fabricator on To make reads how many are made, a loader on To
// load how many stand ready and how many have gone, a receiver how many are
// counted in. ONE place for the words, because three card designs draw them
// and a sentence written in three places drifts in exactly one.
//
// `progressText` takes the four numbers, so a card's WHOLE-transfer line (the
// one beside the green bar) and its per-item lines read identically.
// `neverArrived` is said only on a transfer CLOSED SHORT -- the same rule as
// endedCardSignals(): on a transfer ended "nothing more coming" a truck may
// still be on the road (#600), and calling that lost would be false.
export function progressText(stage, p, { status = null } = {}) {
  const requested = num(p?.requested);
  const ready = num(p?.ready);
  const shipped = num(p?.shipped);
  const received = num(p?.received);
  switch (stage) {
    case 'to_make':
      return makeWords({ ready, requested, made: p?.made });
    case 'to_load':
      return `${Math.max(ready - shipped, 0)} ready now · ${shipped} of ${requested} sent`;
    case 'owed':
      return `${received} of ${requested} counted in`;
    case 'finished': {
      const lost = status === 'closed_short' ? Math.max(shipped - received, 0) : 0;
      return `${received} of ${requested} received${lost > 0 ? ` · ${lost} never arrived` : ''}`;
    }
    default:
      // Awaiting confirmation, and any tab not named above: nothing has
      // happened to the item yet, so the only true number is what was asked.
      return `${requested} asked`;
  }
}

// #713, 682 D11, Session 79.22b.6c.3 -- "MADE" COUNTS PRODUCTION RUNS ONLY.
// With goods sent from the shelf the line says what is READY and splits it:
// "30 of 60 ready · 10 made, 20 from the shelf". Without them it is gate 665's
// line, unchanged: "30 of 60 made". `made` unknown (the runs were not read)
// reads as before, so a failed read changes no card's words. One place, because
// the card's lines, its whole-transfer line and the sign-off answer all say it.
export function makeWords({ ready, requested, made = null }) {
  const r = num(ready);
  const q = num(requested);
  if (made == null) return `${r} of ${q} made`;
  const m = num(made);
  const shelf = r - m;
  if (shelf > 0) return `${r} of ${q} ready · ${m} made, ${shelf} from the shelf`;
  return `${m} of ${q} made`;
}

// A refused item shows "refused" and never a count (gate 665 X2): its numbers
// are frozen at zero forever and would read as work nobody is doing.
// #698 (b), 79.22b.4.7.1 -- one the ASKING branch turned down reads "not
// needed" instead: on San Antonio's work card "refused" reads as San Antonio's
// own refusal (#572). `opts.transfer` says which; without it, as before.
export function lineProgressText(stage, line, opts = {}) {
  if (line?.line_status === 'refused') return opts.transfer && refusedByAsker(line, opts.transfer) ? 'not needed' : 'refused';
  return progressText(stage, lineProgress(line), opts);
}

// #665, gate 665 D15, Session 79.22b.4.4 -- THE GIVING SHELF, BEFORE AND AFTER.
//
// A manager agreeing to hand over stock reads what their shelf holds now, what
// it holds after, and where it reorders -- SAP's availability check at the
// moment of commitment, and the research's hold-back rule. It is a WARNING and
// never a gate: stock may go negative (CLAUDE.md), and the manager decides.
//
// ONE sentence for the Requests inbox card and the Awaiting confirmation card,
// so the two approval screens cannot disagree. `state` is the fetch's own:
// 'loading' and 'failed' say so in words rather than showing a zero that looks
// like an empty shelf. A null reorder point says nothing about reordering and is
// never amber (gate X2). A missing stock row is a shelf holding none.
//
// #721, Session 79.22b.5.1b -- AND WHAT THE BRANCH HAS ALREADY PROMISED
// (`promised`, from promisedFromShelf()) comes off "after", and is named:
// "San Antonio has 45 on the shelf, 20 already promised → 15 after". With
// nothing promised the sentence is exactly what it was (the approved drawing).
// The caller passes state 'failed' when it could not read the promises either,
// so a figure that silently leaves them out is never shown.
export function approvalShelfLine({ qty, giver, stock = null, isMake = false, state = 'ok', promised = 0 }) {
  const who = giver || 'the other branch';
  if (isMake) return { text: `made to order at ${who}`, amber: false, warning: null };
  if (state === 'loading') return { text: `checking ${who}’s shelf…`, amber: false, warning: null };
  if (state === 'failed') return { text: `couldn’t check ${who}’s shelf`, amber: false, warning: null };
  const now = num(stock?.qty_on_hand);
  const owed = Math.max(num(promised), 0);
  const after = now - owed - num(qty);
  const rp = stock?.reorder_point;
  const hasRp = rp !== null && rp !== undefined && rp !== '' && Number.isFinite(Number(rp));
  const amber = hasRp && after < Number(rp);
  const has = owed > 0 ? `${now} on the shelf, ${owed} already promised` : `${now}`;
  return {
    text: `${who} has ${has} → ${after} after${hasRp ? ` · reorders at ${Number(rp)}` : ''}`,
    amber,
    warning: amber ? `${who} would be below its reorder point after this.` : null,
  };
}

export function transferProgress(lines) {
  // #577, Session 79.21b -- AGREED items only, from the one shared place in
  // branchTransfers.js. A refused item is never "to come", so a partly refused
  // transfer no longer reads "50 of 100 sent · 50 still to come" forever.
  const rows = agreedLines(lines);
  const totals = rows.reduce((acc, l) => {
    const p = lineProgress(l);
    acc.requested += p.requested;
    acc.ready += p.ready;
    acc.shipped += p.shipped;
    acc.received += p.received;
    return acc;
  }, { requested: 0, ready: 0, shipped: 0, received: 0 });
  const outstanding = Math.max(totals.requested - totals.received, 0);
  const inTransit = Math.max(totals.shipped - totals.received, 0);
  // ONE DEFINITION OF THE MAKE ARITHMETIC, and it lives in branchTransfers.js
  // because `productionQueue()` needs it there and that module cannot import
  // this one without a cycle. Delegated rather than re-added here: when the
  // same sum is written in two files it drifts in exactly one of them, which is
  // this project's most-repeated finding.
  //
  // SUMMED PER LINE RATHER THAN DERIVED FROM THESE TOTALS — and the honest
  // reason is narrower than it first looks.
  //
  // While every line obeys `ready <= requested` the two forms are ALGEBRAICALLY
  // IDENTICAL, because each per-line difference is already non-negative and the
  // outer max() never bites. The first version of this comment claimed a
  // transfer with one line finished and another untouched would report nothing
  // left to make; that is FALSE, and the test written to prove it passed with
  // the wrong implementation planted, which is how it was caught.
  //
  // They diverge only on data the database FORBIDS — a line signed off beyond
  // what was asked for. Then per-line clamps that line at zero, while
  // derived-from-totals lets its surplus CANCEL another line's outstanding
  // work and quietly hides real work to do. The constraint makes that
  // unreachable through the app; this shape stays because a screen also renders
  // fixtures, optimistic updates and rows fetched before a constraint existed,
  // and the safe direction is to over-report work rather than lose it.
  const make = makeProgress(lines);
  return {
    ...totals,
    toMake: make.toMake,
    readyToLoad: make.readyToLoad,
    outstanding,
    inTransit,
    // DERIVED, NEVER STORED — D49. Something has gone and something has not.
    partiallyShipped: totals.shipped > 0 && totals.shipped < totals.requested,
    complete: totals.requested > 0 && totals.received >= totals.requested,
  };
}

// The requester's tracker — D52, and it is FOUR STAGES for every row because
// #391's D5 says so. A transfer needs the same four a material request needs
// and exactly one word differs, which is the whole reason both documents can
// share one page.
export const TRANSFER_STAGES = ['Sent', 'Approval', 'Shipped', 'Received'];

// Which stage is lit, and what the Shipped stage says underneath it.
//
// THE GAP IS SHOWN INSIDE THE SHIPPED STAGE rather than as a stage of its own:
// "250 of 400 sent — 150 still to come", under a marker that is lit but not
// finished. The four stage names never change and no row gets a different shape
// from any other row.
export function shipmentStage(transfer, lines) {
  const p = transferProgress(lines);
  const status = transfer?.status || 'requested';

  if (status === 'received') return { index: 3, caption: null, progress: p, done: true };
  if (status === 'denied' || status === 'cancelled') {
    return { index: 1, caption: null, progress: p, done: true, stopped: true };
  }
  if (status === UNFILLED_STATUS) {
    return {
      index: 2,
      caption: p.received > 0
        ? `${p.received} of ${p.requested} arrived · nothing more is coming`
        : 'Nothing more is coming',
      progress: p,
      done: true,
      stopped: true,
    };
  }
  if (status === 'closed_short') {
    return {
      index: 2,
      caption: `${p.received} of ${p.requested} arrived · the rest did not make it`,
      progress: p,
      done: true,
      stopped: true,
    };
  }
  if (status === 'in_transit') {
    const caption = p.shipped < p.requested
      ? `${p.shipped} of ${p.requested} sent · ${p.requested - p.shipped} still to come`
      : `${p.received} of ${p.requested} arrived`;
    return { index: 2, caption, progress: p, done: false };
  }
  if (status === 'approved') return { index: 1, caption: null, progress: p, done: false };
  return { index: 0, caption: null, progress: p, done: false };
}

// D67 -- a line's own promised date wins, otherwise the transfer's. The rule
// lives in branchTransfers.js since #618 (Session 79.22b), so the requester's
// card reads the same one; it is re-exported here for this file's importers.
export { promisedDateFor };

// D63 — MISSING and EXTRA, which are NetSuite's own two words and the concept
// freight calls OS&D. A gap in either direction is a fact about this load, at
// the handover, which is where the evidence is.
// #586, Session 79.21b -- NOTHING CAN BE CHOSEN IS NOT NOTHING CHOSEN. With an
// empty rack every line's ceiling is zero, and the load sheet asked the driver
// to "type how many are going on this truck" when the most they could type was
// nought. Gate 553's edge case draws the sheet saying what is true instead.
export function nothingStandingToSend(ceilings) {
  const vals = Array.isArray(ceilings) ? ceilings : Object.values(ceilings || {});
  return vals.length > 0 && vals.every(v => (Number(v) || 0) <= 0);
}

// Made goods and shelf goods are different sentences: "nothing has been made
// yet" is wrong about a shelf nobody makes anything for.
export function nothingStandingMessage(transfer) {
  return transfer?.fulfilment_mode === 'make'
    ? 'Nothing has been made yet.'
    : 'Nothing is standing here to send yet.';
}

// #682, amended at 79.22b.6c.2b's open -- CAN THE SENDING BRANCH PUT THIS
// TRUCK RIGHT? The database's own test, copied clause for clause from
// count_in_load() and correct_load() (20261006_correct_truck_and_count_682.sql,
// statements 13 and 10): the first truck leaving where the goods stood -- no
// earlier truck, leg 1, leaving the transfer's sending branch -- and NOT a
// hub's goods collected by the branch they are for, which is the sending
// branch being a transfer hub while the truck's loader works at another branch.
// Only such a truck can be corrected (682 D2, narrowed), and only such a truck,
// counted above its sheet at the destination, asks its sender about the extra
// (682 D7); anywhere else a count above it is refused.
//
// The database writes the loader clause as IS DISTINCT FROM, so a truck with no
// recorded loader, or one whose loader has no branch, counts as collected by
// another branch: refused, never asked. The loader's branch and the hub flag
// ride on the truck itself (LOAD_SELECT); the flag is the truck's own
// from-branch's, which the clause before has just made the sending branch, so
// it is the flag the database reads. leg_no and is_transfer_hub are NOT NULL on
// both databases, so they have no unknown case to decide.
export function senderCanPutRight(load, transfer) {
  if (!load || !transfer) return false;
  if (load.prev_load_id != null) return false;
  if (load.leg_no != null && Number(load.leg_no) !== 1) return false;
  if (String(load.from_branch_id) !== String(transfer.from_branch_id)) return false;
  if (load.from_hub?.is_transfer_hub === true) {
    const loaderBranch = load.loader?.branch_id;
    if (loaderBranch == null || String(loaderBranch) !== String(load.from_branch_id)) return false;
  }
  return true;
}

// #587, Session 79.21b -- AT THE DESTINATION, NOTHING IS BOOKED ABOVE WHAT WAS
// SENT. Gate 371 D10: "hard reject, at the database" -- an overage means the
// sender miscounted, and it is put right on the sender's side. The count-in
// screen previewed "extra" as something it would record and book, which is
// right at a handover yard (517 D63), where nothing is booked, and wrong here,
// where the database then refused the WHOLE count with its own rule name.
// Caught before the press, in words that say what to do. Null when the count
// may go ahead.
//
// #682, Session 79.22b.6c.2b (682 D7, D8) -- NOW ONLY WHERE THE DATABASE REFUSES.
// At the destination, on a truck its sending branch can put right, a count
// above the truck goes ahead: the truck's number is booked and the sender is
// asked about the rest (overCountAsk() below says so before the press). At a
// stop on the way, which this used to wave through with D63's "extra" wording,
// count_in_load() and hand_over_load() now refuse a count above the truck in
// these same words, so the screen says them before the press instead.
export function overCountRefusal({ load, transfer, preview, branchNames = {} }) {
  if (!load || !transfer || !preview || !(preview.extra > 0)) return null;
  if (String(load.to_branch_id) === String(transfer.to_branch_id) && senderCanPutRight(load, transfer)) return null;
  const sender = branchNames[String(load.from_branch_id)]
    || branchNames[String(transfer.from_branch_id)] || 'The sending branch';
  return {
    extra: preview.extra,
    notice: `Only what was sent can be booked in here. ${sender} sent ${preview.extra} more than it recorded, `
      + `so its count is ${preview.extra} too high — tell ${sender} so it can put that right.`,
    blockedReason: 'Change the count to no more than was sent.',
  };
}

const qtyWords = n => String(Math.round((Number(n) || 0) * 100) / 100);

// #682, Session 79.22b.6c.2b -- P4 and callout I: BEFORE THE PRESS, COUNT IN SAYS
// WHAT IT WILL DO with a count above the truck's sheet, where the sending branch
// will be asked (682 D7) -- "4 counted — 2 more than Houston's load sheet. Count
// in books in the 2 it carried and asks Houston about the other 2." The button
// keeps its name. With several items on the truck the notice names each line
// above (F1, "the notice names each line above"); one item reads as drawn.
// Null where nothing is above the truck, or where the count is refused instead.
export function overCountAsk({ load, transfer, preview, branchNames = {}, items = {} }) {
  if (!load || !transfer || !preview || !(preview.extra > 0)) return null;
  if (String(load.to_branch_id) !== String(transfer.to_branch_id) || !senderCanPutRight(load, transfer)) return null;
  const sender = branchNames[String(load.from_branch_id)]
    || branchNames[String(transfer.from_branch_id)] || 'The sending branch';
  const rows = load.branch_transfer_load_items || [];
  const nameOf = id => rows.find(r => String(r.item_id) === String(id))?.inventory_items?.name
    || items[String(id)]?.name || 'Item';
  const one = l => `${qtyWords(l.arrived)} counted — ${qtyWords(l.gap)} more than ${sender}'s load sheet. `
    + `Count in books in the ${qtyWords(l.loaded)} it carried and asks ${sender} about the other ${qtyWords(l.gap)}.`;
  const over = (preview.lines || []).filter(l => l.kind === 'extra');
  return {
    extra: preview.extra,
    notice: rows.length > 1 ? over.map(l => `${nameOf(l.item_id)}: ${one(l)}`).join(' ') : one(over[0]),
  };
}

// P5 and callout J: AFTER THE PRESS, what was booked and that the sender has been
// asked -- "2 booked in. Houston has been asked to confirm the other 2." From the
// database's own answer: `asked` names each question it opened (count_in_load(),
// statement 13). Null when the count asked nothing.
export function countAskedSentence({ booked, asked = [], sender }) {
  const extra = (Array.isArray(asked) ? asked : []).reduce((n, a) => n + (Number(a?.extra) || 0), 0);
  if (!(extra > 0)) return null;
  return `${qtyWords(booked)} booked in. ${sender} has been asked to confirm the other ${qtyWords(extra)}.`;
}

// WHAT A COUNT BOOKS IN ALTOGETHER, for its answer: each line's bookedOnArrival().
export function bookedTotal(load, counts) {
  const typed = countsPayload(counts) || {};
  return (load?.branch_transfer_load_items || []).reduce((n, li) => n + bookedOnArrival(li, typed), 0);
}

export function discrepancy(loadItems) {
  const rows = Array.isArray(loadItems) ? loadItems : [];
  let missing = 0;
  let extra = 0;
  const lines = [];
  for (const li of rows) {
    // A load still on the road has not been counted. Treating null as zero
    // would report every in-transit load as entirely missing.
    if (li?.qty_arrived == null) continue;
    const loaded = num(li.qty_loaded);
    const arrived = num(li.qty_arrived);
    const gap = arrived - loaded;
    if (gap === 0) continue;
    if (gap < 0) missing += -gap; else extra += gap;
    lines.push({ item_id: li.item_id, loaded, arrived, gap, kind: gap < 0 ? 'missing' : 'extra' });
  }
  return { missing, extra, lines, clean: lines.length === 0 };
}

// THE COUNTS PAYLOAD, AND IT HAS ITS OWN FUNCTION FOR A REASON WORTH READING.
//
// arrive_transfer_load() reads a count as `p_counts -> '<item_id>' ->> 'qty'`
// and falls back to qty_loaded when it finds nothing — which is D63 working as
// designed: no count supplied means nothing was wrong, and the ordinary
// Wednesday costs the driver nothing.
//
// The consequence is that A MALFORMED PAYLOAD IS INDISTINGUISHABLE FROM
// "EVERYTHING ARRIVED". Key it by index instead of item id, or nest `qty` one
// level wrong, and every discrepancy silently disappears with no error raised
// anywhere. That is this project's most-repeated defect shape — a failure that
// reports success — so the shape is built in one place and pinned by a test
// rather than assembled inline at the call site.
//
// Takes [{ item_id, counted }] and returns null when nothing was counted, so
// the common case sends no payload at all.
export function countsPayload(rows) {
  const out = {};
  let any = false;
  for (const r of (Array.isArray(rows) ? rows : [])) {
    if (r?.item_id == null) continue;
    if (r.counted === '' || r.counted == null) continue;
    const n = Number(r.counted);
    if (!Number.isFinite(n) || n < 0) continue;
    out[String(r.item_id)] = { qty: n };
    any = true;
  }
  return any ? out : null;
}

// D66, Colby's ask — SOONEST FIRST, because the driver's real question is what
// has to move today.
//
// A load waiting at a place that is not a branch's own yard is the Austin case,
// and it appears in the queue rather than needing to be remembered.
export function driverQueue(loads, branchId, { transfersById = {} } = {}) {
  const rows = Array.isArray(loads) ? loads : [];
  return sortByPromised(
    rows.filter(l => l.status === 'in_transit' && String(l.to_branch_id) === String(branchId)),
    { transfersById });
}

// THE SORT ON ITS OWN, because the filter and the ordering answer different
// questions and one of them is wrong for the hub.
//
// driverQueue() keeps "addressed to my branch", which is right for a delivery
// arriving here. A load waiting at Austin FOR Dallas is addressed to AUSTIN, so
// running it through that filter would silently return nothing -- an empty tab
// on a screen whose whole job is to stop material going missing at a yard.
// arrivingAt/toCollectFor/movingElsewhere decide membership; this only orders.
export function sortByPromised(loads, { transfersById = {} } = {}) {
  const rows = Array.isArray(loads) ? loads : [];
  return rows
    .map(l => {
      const t = transfersById[String(l.transfer_id)] || l.branch_transfers || null;
      const lines = t?.branch_transfer_items || [];
      const dates = lines
        .map(li => promisedDateFor(li, t))
        .filter(Boolean)
        .sort();
      return { ...l, __transfer: t, __promisedDate: dates[0] || null };
    })
    .sort((a, b) => {
      // No promised date sorts last rather than first: an undated load is not
      // urgent, and sorting null to the top would bury the ones that are.
      if (!a.__promisedDate && !b.__promisedDate) return num(a.id) - num(b.id);
      if (!a.__promisedDate) return 1;
      if (!b.__promisedDate) return -1;
      if (a.__promisedDate !== b.__promisedDate) return a.__promisedDate < b.__promisedDate ? -1 : 1;
      return num(a.id) - num(b.id);
    });
}

// D65 — A TEAM MEMBER NEVER SEES THE VALUE OF WHAT THEY ARE LOADING.
//
// Colby asked outright and the answer is no. This delegates to the predicate
// rather than restating the role set, per locked rule 8, because #77 depends on
// canSeeInventoryCost holding and a second copy of a permission is a second
// copy that can drift.
//
// It is written down as a decision rather than left as an assumption because
// #515's D39 is this exact defect one screen over: the link to change a cost
// was gated to manager and above, the FIELD was gated to nothing, and a team
// member saw a figure and could type over it.
export function canSeeLoadValue(auth) {
  return canSeeInventoryCost(auth);
}

// What a load is worth, at the cost that travelled with it.
//
// Returns null rather than 0 when nothing on the load carries a cost, because
// "we do not know" and "nothing" are different answers and a screen showing
// $0.00 for the first is lying.
export function loadValue(loadItems) {
  const rows = Array.isArray(loadItems) ? loadItems : [];
  let value = 0;
  let priced = false;
  let anyUnpriced = false;
  for (const li of rows) {
    const qty = li?.qty_arrived == null ? num(li?.qty_loaded) : num(li.qty_arrived);
    const cost = li?.unit_cost_at_transfer;
    if (cost == null || !Number.isFinite(Number(cost))) { anyUnpriced = true; continue; }
    value += qty * Number(cost);
    priced = true;
  }
  return priced ? { value, anyUnpriced } : { value: null, anyUnpriced };
}

// A transfer can take another load while anything is still owed on it. NOT
// "while it is approved" — that was the old one-shot rule, and it is why a
// second truck had nothing to run against.
export function isLoadable(transfer, lines) {
  if (!transfer) return false;
  if (!['approved', 'in_transit'].includes(transfer.status)) return false;
  // #580 part a -- a request for the branch's own shelf has nothing to load;
  // the database refuses it too (gate 553 D21).
  if (isOwnShelf(transfer)) return false;
  const p = transferProgress(lines);
  return p.requested > p.shipped;
}

// #628, Session 79.22b -- IS ANYTHING READY FOR A TRUCK AT THE SENDING BRANCH
// RIGHT NOW. isLoadable() above answers whether a transfer may take ANOTHER
// load, and it is right for that. This answers whether there is anything to put
// on one: made, or on the rack for a shelf transfer, and not yet sent.
//
// The To load tab and the transfer sheet each carried their own copy of this
// question, and they disagreed: the sheet asked isLoadable() alone, told a
// fabricator "This is ready to load" of a make request with 77 owed and none
// made, and pointed him at a tab that correctly did not list it. One rule, two
// readers, so they cannot disagree again.
export function hasReadyToLoad(transfer, lines) {
  return isLoadable(transfer, lines) && transferProgress(lines).readyToLoad > 0;
}

// #628 half 2, Session 79.22b -- APPROVED BY COLBY AS DRAWN. CAN THE SENDING
// BRANCH'S SHELF FILL A MADE-TO-ORDER REQUEST RIGHT NOW. Houston holding 675
// stakes could not send 12 on a make request without first signing off a
// production run nobody did. `shelfFor(itemId)` is what the sending branch
// holds. This says only what is TRUE of the transfer; who is shown it is the
// caller's question - canStateHowFilled(), because a driver is never asked
// how a line was filled (gate 517 D64).
export function shelfFillable(transfer, lines, shelfFor) {
  if (!transfer || transfer.fulfilment_mode !== 'make') return false;
  if (!isLoadable(transfer, lines)) return false;
  const holds = typeof shelfFor === 'function' ? shelfFor : () => 0;
  return agreedLines(lines).some(l =>
    num(l.qty_requested) - num(l.qty_shipped) > 0 && num(holds(l.item_id)) > 0);
}

// The To load card's line for a row listed only because the shelf can fill
// it - the approved "Nothing made · 12 on Houston's shelf". One owed item
// names its count; several say how many of them that shelf holds.
//
// #721 (drawing B, approved at 79.22b.5.1b) -- `promisedFor(itemId)` is what
// that shelf has already promised to OTHER transfers (frames made for Dallas
// wait on Houston's shelf for their truck), named beside the count: "675 on
// Houston's shelf, 100 already promised". Whether the row is listed at all is
// unchanged (shelfFillable above); only its words are.
export function shelfNoteFor(transfer, lines, shelfFor, branchNames = {}, promisedFor = () => 0) {
  const holds = typeof shelfFor === 'function' ? shelfFor : () => 0;
  const owedTo = typeof promisedFor === 'function' ? promisedFor : () => 0;
  const owed = agreedLines(lines).filter(l => num(l.qty_requested) - num(l.qty_shipped) > 0);
  const on = owed.filter(l => num(holds(l.item_id)) > 0);
  if (!on.length) return null;
  const where = `${branchNames[String(transfer?.from_branch_id)] || 'the sending branch'}'s shelf`;
  if (owed.length !== 1) return `${on.length} of ${owed.length} items on ${where}`;
  const promised = num(owedTo(on[0].item_id));
  return `${num(holds(on[0].item_id))} on ${where}${promised > 0 ? `, ${promised} already promised` : ''}`;
}

// #628 (acceptance line F8) -- DELIVERED IS NOT STANDING. standingAt() adds
// what arrived at a place and subtracts what left it, which is right at a hub
// and wrong at the transfer's own destination, because nothing ever leaves the
// destination: once a load was counted in at Dallas, Dallas read as holding it
// "standing" for ever and the sheet offered a Send that could only fail. The
// destination is where a transfer ends, never a place goods wait to move on
// from -- which is also how hubsWaitingFor() below already reads it.
export function standingForOnwardLoad(loads, lines, transfer, branchId) {
  if (branchId == null || String(transfer?.to_branch_id) === String(branchId)) return false;
  return (lines || []).some(l => standingAt(loads, l.item_id, branchId) > 0);
}

// #634, Session 79.22b -- THE RECEIVING BRANCH'S NEW AVERAGE, per item, for a
// load being counted in at its destination. Gate 371 D16: "cost travels, and
// the receiver's average is recalculated on arrival". That apparatus existed
// and was orphaned at 79.15f, when counting-in replaced the one-shot receive
// and the cost half was not carried across -- so Dallas holding 248 stakes at
// $4.75 took 12 at $5.25 and still read $4.75.
//
// ONE FORMULA. It is receivedAverageCost(), which is the purchasing receive's
// own computeNewAverageCost() -- D16 forbids a second copy, and #240 is why the
// app computes it and the database only writes it.
//
// THE QUANTITY IS WHAT IS BOOKED IN, the same value count_in_load() books: the
// typed count where there is one, otherwise what was loaded -- and NEVER MORE
// THAN THE TRUCK CARRIED (#682, 682 D7, 79.22b.6c.2b): a count above the truck
// books the truck's number and asks the sender about the rest, so blending the
// typed 4 into the average when 2 landed would price stock that is on nobody's
// shelf. An item whose cost is unknown is LEFT OUT, so its average is not
// touched at all (G6); so is one counted at nothing, because no stock lands.
// Returns null when there is nothing to write, which the database reads as
// "change no average".
export function averagesOnArrival({ loadItems = [], counts = null, stock = [] } = {}) {
  const shelf = new Map((Array.isArray(stock) ? stock : []).map(s => [String(s.item_id), s]));
  const typed = countsPayload(counts) || {};
  const out = {};
  for (const li of (Array.isArray(loadItems) ? loadItems : [])) {
    if (li?.item_id == null) continue;
    const key = String(li.item_id);
    const qty = bookedOnArrival(li, typed);
    if (!(qty > 0)) continue;
    const cost = li.unit_cost_at_transfer;
    if (cost == null || !Number.isFinite(Number(cost))) continue;
    const here = shelf.get(key);
    const next = receivedAverageCost({
      currentQty: here?.qty_on_hand ?? 0,
      currentAvg: here?.avg_unit_cost ?? null,
      receivedQty: qty,
      unitCostAtTransfer: Number(cost),
    });
    if (next != null && Number.isFinite(Number(next)) && Number(next) >= 0) out[key] = Number(next);
  }
  return Object.keys(out).length ? out : null;
}

// WHAT A COUNT BOOKS IN FOR ONE TRUCK LINE, at the destination: count_in_load()'s
// own LEAST(counted, qty_loaded) (statement 13). `typed` is countsPayload()'s
// shape. One copy, for the average above and for the arrival bell and email,
// which name the number booked (the gate's B5: "with the number booked").
export function bookedOnArrival(li, typed = {}) {
  const loaded = num(li?.qty_loaded);
  const key = String(li?.item_id);
  const counted = typed && typed[key] ? num(typed[key].qty) : loaded;
  return Math.min(counted, loaded);
}

// #655, Session 79.22b -- WHAT IS ADDRESSED HERE TO COUNT IN, for one transfer.
// #628's shape at the receiving end: "may this be received" was asked of the
// TRANSFER (on the road, you are at the destination), so a Dallas driver whose
// material was waiting at Austin was told "This is arriving here" and sent to a
// To count in tab that did not list it. The same rule that tab applies through
// arrivingAt() -- a load addressed to this branch, on the road or here and not
// yet counted -- with the transfer handed in, because the sheet's own loads
// carry no embedded transfer for arrivingAt() to read.
export function arrivingFor(loads, transfer, branchId) {
  if (branchId == null || String(transfer?.to_branch_id) !== String(branchId)) return { loads: [], qty: 0 };
  const rows = (Array.isArray(loads) ? loads : []).filter(l =>
    (l?.transfer_id == null || transfer?.id == null || String(l.transfer_id) === String(transfer.id)) &&
    String(l?.to_branch_id) === String(branchId) &&
    (l?.status === 'in_transit' || (l?.status === 'arrived' && !l?.counted_at)));
  const qty = rows.reduce((n, l) => n + (l.branch_transfer_load_items || l.items || [])
    .reduce((m, li) => m + num(li?.qty_arrived == null ? li?.qty_loaded : li.qty_arrived), 0), 0);
  return { loads: rows, qty };
}

// D56 — the balance can be written off only while the request is live, and only
// with a reason. Mirrors close_branch_transfer_unfilled()'s own guard.
//
// Not for a request a branch made for its own shelf: it ends at its last run,
// or its asker withdraws it (gate 371 D32) - "nothing more is coming" is a
// supplier telling somebody else, and here there is nobody else.
export function isUnfillable(transfer, lines) {
  if (!transfer) return false;
  if (!['approved', 'in_transit'].includes(transfer.status)) return false;
  if (isOwnShelf(transfer)) return false;
  const p = transferProgress(lines);
  return p.requested > p.shipped;
}

// ─────────────────────────────────────────────────────────────────────────────
// DATABASE OPERATIONS
//
// Every multi-table write goes through a Postgres function called with
// supabase.rpc(), per locked rule 7. All three stay SECURITY INVOKER — as the
// caller — which is gate D4 and #371's enforcement rather than a default:
// inventory_transactions' insert rule ties every stock row to the caller and to
// the caller's own branch, so the sender can only write their leg and the
// receiver theirs.
// ─────────────────────────────────────────────────────────────────────────────

// #682, amended at 79.22b.6c.2b's open -- AND WHO LOADED IT WORKS WHERE, AND
// WHETHER IT LEFT A HUB: the two facts the database's own "can the sending
// branch put this truck right?" reads (senderCanPutRight() below), arriving
// WITH the truck rather than after it, so no screen offers Correct or promises
// a question for a moment and then withdraws it. Both links are named by their
// constraint, identical on both databases (read Oct 7, 2026): the truck has
// six links to `users` and an unnamed embed would be ambiguous.
const LOAD_SELECT =
  '*, branch_transfer_load_items(*, inventory_items(id, name, sku, uom)), '
  + 'loader:users!branch_transfer_loads_loaded_by_fkey(branch_id), '
  + 'from_hub:branches!branch_transfer_loads_from_branch_id_fkey(is_transfer_hub)';

export async function fetchLoads(transferId) {
  if (transferId == null) return { data: [], error: null };
  return supabase
    .from('branch_transfer_loads')
    .select(LOAD_SELECT)
    .eq('transfer_id', transferId)
    .order('load_seq', { ascending: true });
}

// Everything on the road heading for this branch — the driver's queue (D66).
//
// The transfer comes back with it because the queue card names the place and
// the promised date, and a second round trip per card is what makes a list
// feel slow on a phone.
// #600, Session 79.21b -- WHICH TRANSFERS' LOADS THE QUEUES WATCH. Every
// transfer except those whose loads are settled -- and that deliberately
// INCLUDES one that ended "nothing more coming": a truck already on the road
// when it ended still arrives (371 D33, and the app says so), and a pile at a
// hub still needs fetching. It used to be every transfer not finished, and
// closed_unfilled counted as finished, so the load dropped off To count in and
// To collect while the app was saying "what is on the road still arrives".
// fetchOpenLoads() keeps only loads not yet counted, so a transfer whose
// trucks are all in costs nothing here.
const LOADS_SETTLED = ['received', 'denied', 'cancelled', 'closed_short'];
export function transferIdsWithLoadsToWatch(transfers) {
  return (Array.isArray(transfers) ? transfers : [])
    .filter(t => t && t.id != null && !LOADS_SETTLED.includes(t.status))
    .map(t => t.id);
}

export async function fetchOpenLoads(branchId, transferIds = null) {
  if (branchId == null) return { data: [], error: null };
  // EVERY LOAD STILL MOVING, not only the ones addressed to this branch.
  //
  // IT USED TO FILTER ON `to_branch_id = me`, AND THAT IS WHAT HID THE HUB.
  // A load Houston drops at Austin for Dallas to collect is addressed to
  // AUSTIN, so Dallas never saw it -- the material sat at a yard with nobody
  // employed there, on nobody's screen, while the request stayed open.
  //
  // The three questions a branch asks are answered by SPLITTING this list
  // rather than by three round trips: is it coming to me (count it in), is it
  // waiting at a hub for me (go and get it), or is it somebody else's truck
  // (watch only). Only the transfer knows which, so it comes back with it.
  // ** IT USED TO RETURN ONLY `in_transit`, AND THAT WOULD HAVE LOST MATERIAL. **
  //
  // Colby, 79.10: "houston may leave 200 frames at the hub... but the dallas
  // driver may only be able to load and bring 100 frames back with him that
  // day." The moment he takes 100, the leg that brought all 200 to Austin is
  // ARRIVED -- so a queue keyed on `in_transit` would have dropped the other
  // 100 off every screen in the company, standing at a yard with nobody
  // employed there. That is precisely the defect this queue exists to fix,
  // reintroduced one layer down.
  //
  // Scoped by TRANSFER rather than by load status, so the arithmetic below has
  // the complete picture: what is standing somewhere is what arrived there
  // minus what has already left, and a missing outbound leg overstates it.
  // `transferIds` is the caller's list of transfers that are still open, which
  // it already has -- cheaper and safer than filtering through the embedded
  // relation, which PostgREST answers with silent emptiness when it goes wrong.
  //
  // #694, Session 79.22b.4.6.1 -- UNDONE TRUCKS COME BACK TOO, for one reader
  // only: the To load card previews the next load number from this list
  // (nextLoadSeq, #671), and a truck its sender took back still owns its
  // number. Every other reader of this list asks for `in_transit` or `arrived`
  // by name, so an undone truck is on none of the lists it builds.
  // #608 / gate 675 D17 -- with its count, so a list cut at the database's
  // row limit says so (cutOff() in readLimit.js) instead of losing trucks.
  let q = supabase
    .from('branch_transfer_loads')
    .select(`${LOAD_SELECT}, branch_transfers(*, branch_transfer_items(*, inventory_items(id, name, sku, uom)))`, { count: 'exact' })
    .in('status', ['in_transit', 'arrived', 'undone']);
  if (Array.isArray(transferIds)) {
    if (transferIds.length === 0) return { data: [], error: null };
    q = q.in('transfer_id', transferIds);
  }
  return q;
}

// ── THE THREE QUESTIONS, ANSWERED FROM ONE LIST ─────────────────────────────
//
// Every one takes the LOAD's destination and the TRANSFER's destination as two
// different things, because that difference is the whole of the hub case.
//
//   arrivingAt   the load is addressed here AND here is where it was going.
//                Somebody counts it in and stock lands.
//   toCollectFor the load is addressed somewhere else, but the REQUEST is
//                mine. It is sitting at a hub and my branch has to fetch it.
//   elsewhere    neither. Not my job, and the tab that holds it is grey.
const finalStop = (load) =>
  load?.branch_transfers?.to_branch_id ?? load?.__transfer?.to_branch_id ?? null;

// ** A FORGOTTEN EVENING TAP MUST NOT BLOCK THE MORNING CREW — #553 D17. **
//
// This used to ask only for `in_transit`, which was right while pressing arrive
// WAS the count. It is not any more: arriving and counting are two events, and
// between them sits a truck in the yard with nothing counted.
//
// Colby's design, his words: the queue lists loads that are DUE whether or not
// anybody tapped arrived. So a driver who gets back at 8:40pm and goes home
// without pressing anything cannot hide the load from the people who unload it
// the next morning — it is in the queue either way, and counting it in fills
// the arrival time itself.
//
// WHAT LEAVES THE QUEUE IS THE COUNT, NEVER THE ARRIVAL. `counted_at` is the
// only thing that takes a row off this list, which is why it is the column the
// filter reads. Keying it on `status === 'arrived'` instead would empty the
// queue the moment somebody tapped, which is the defect one layer down.
export function arrivingAt(loads, branchId) {
  return (Array.isArray(loads) ? loads : []).filter(l =>
    (l.status === 'in_transit' || (l.status === 'arrived' && !l.counted_at)) &&
    String(l.to_branch_id) === String(branchId) &&
    String(finalStop(l)) === String(branchId));
}

// Has the truck pulled in but nobody opened the boxes yet? That is the state
// the old model could not represent, and the row reads differently for it.
export function isAwaitingCount(load) {
  return load?.status === 'arrived' && !load?.counted_at;
}

// #674, Session 79.22b.4 -- HOW MANY ARE HERE, UNCOUNTED, AT THE END OF THE
// JOURNEY. "Received" means counted in, so a truck that pulled in and was
// marked "It is here -- count it later" still counts as NOT received, and the
// sheet told a driver standing beside 38 stakes that "40 never arrived" and
// invited a write-off of all 40. Units waiting to be counted at the transfer's
// own destination are here, not lost; until they are counted, nothing about
// the gap is final. A load resting at a hub is not this -- it is still going.
export function awaitingCountUnits(loads, transfer) {
  if (!transfer) return 0;
  let n = 0;
  for (const l of (Array.isArray(loads) ? loads : [])) {
    if (!isAwaitingCount(l) || String(l.to_branch_id) !== String(transfer.to_branch_id)) continue;
    for (const li of (l.branch_transfer_load_items || l.items || [])) n += num(li.qty_loaded);
  }
  return n;
}

// #741, Session 79.22b.6e -- WHERE ONE ITEM'S UNBOOKED UNITS ACTUALLY ARE. A
// finished transfer said "40 never arrived", in the loss red, while a truck
// carrying them was still on the road - #674 taught the write-off sentence and
// the card this, and the item column was not in its sweep. Read from the
// trucks: on a moving load, arrived at the destination and not yet counted
// (awaitingCountUnits' rule), or standing at a pickup point (the hub rows'
// `standing`; what is still rolling there is already on a moving load).
export function itemInFlight(loads, transfer, itemId, branchNames = {}) {
  const all = Array.isArray(loads) ? loads : [];
  const qtyOf = (l) => (l.branch_transfer_load_items || l.items || [])
    .filter(li => String(li.item_id) === String(itemId))
    .reduce((n, li) => n + num(li.qty_loaded), 0);
  let moving = 0;
  let waiting = 0;
  for (const l of all) {
    if (l?.status === 'in_transit') moving += qtyOf(l);
    else if (isAwaitingCount(l) && String(l.to_branch_id) === String(transfer?.to_branch_id)) waiting += qtyOf(l);
  }
  const standing = [];
  for (const hub of hubsWaitingFor(all, transfer)) {
    const n = (hub.rows || []).filter(r => String(r.item_id) === String(itemId))
      .reduce((s, r) => s + num(r.standing), 0);
    if (n > 0) {
      standing.push({
        name: hub.branchId != null ? (branchNames[String(hub.branchId)] || 'a pickup point') : (hub.place || 'a pickup point'),
        n,
      });
    }
  }
  return { moving, waiting, standing };
}

// ── HOW A LEG ENDED — #553 D9 ───────────────────────────────────────────────
//
// One value, `arrived`, used to cover three different events with three
// different consequences, and every reader re-derived which by comparing the
// leg's destination to the transfer's. `ended_as` writes it down.
//
// `delivered` finishes and lands stock. `dropped` leaves a pile somewhere for
// somebody to fetch. `handed_over` puts it in a named person's custody. A leg
// still running has no ending, which is what null means.
export const LOAD_ENDINGS = ['delivered', 'dropped', 'handed_over'];

export function endingLabel(load) {
  if (!load) return null;
  if (load.status === 'in_transit') return 'On the road';
  switch (load.ended_as) {
    case 'delivered':   return 'Delivered';
    case 'dropped':     return 'Dropped off';
    case 'handed_over': return 'Handed over';
    // #729, Session 79.22b.6e -- "Here, not counted" was read from any branch.
    default:            return isAwaitingCount(load) ? 'Arrived, not counted' : 'Arrived';
  }
}

// #729, Session 79.22b.6e -- THE COUNT-IN SECTION'S HEADING, the branch named.
// "On its way here" was read by an admin under All branches, where there is no
// here (Colby's #673 ruling: say where a load is going). `branch` is the
// reader's own branch, which every load on that list is heading to; with no
// name yet loaded the old words stand rather than a blank.
export function countInHeading({ count = 0, awaiting = 0, branch = null } = {}) {
  const at = branch ? `at ${branch}` : 'here';
  const to = branch ? `to ${branch}` : 'here';
  if (awaiting === count) {
    return count === 1
      ? `${branch ? `At ${branch}` : 'Here'}, waiting to be counted`
      : `${count} loads ${at}, waiting to be counted`;
  }
  if (awaiting > 0) return `${count} to count in · ${awaiting} already ${at}`;
  return count === 1 ? `On its way ${to}` : `${count} loads on their way ${to}`;
}

// Where a leg started and where it ended, in words, whether either end is a
// branch or a spot on the road — D10. A place is free text the driver typed, so
// it is rendered as given and never looked up.
export function legEnds(load, branchNames = {}) {
  const name = (id, place) => place
    || (id == null ? null : (branchNames[String(id)] || `Branch ${id}`));
  return {
    from: name(load?.from_branch_id, load?.from_place),
    to:   name(load?.to_branch_id, load?.to_place),
    fromIsPlace: !!load?.from_place,
    toIsPlace:   !!load?.to_place,
  };
}

// ── WHAT KIND OF HANDOVER IS THIS, AND IS IT LEGAL AT ALL ───────────────────
//
// ** THIS EXISTS BECAUSE I GOT IT WRONG AND A LIVE TEST CAUGHT IT. ** The first
// version of handOverLoad() refused any call with no INBOUND leg, on the
// reasonable-sounding grounds that a handover needs something to hand over.
// That broke the case Colby asked for at 79.10, in his own words: "the dallas
// driver may only be able to load and bring 100 frames back with him that
// day... so he can pick them up on another day."
//
// COMING BACK FOR THE REST CLOSES NO LEG. Once the first truck has gone, the
// leg that brought the pile to Austin is already `arrived` — there is nothing
// inbound to close, only material standing on the ground and a truck to put
// some of it on. 1,925 tests passed and the build compiled; the press did
// nothing and said nothing, and it was found by pressing it a second time and
// reading the rows back. #492's silent refusal, committed by the session that
// was removing them.
//
// It is pulled out as a pure function so the four combinations can be tested
// without a network — the guard I got wrong was the one thing in that function
// no test could reach.
export function handoverShape({ inbound = 0, taking = 0 } = {}) {
  if (inbound > 0 && taking > 0) {
    return { ok: true, kind: 'handover', reason: null };
  }
  if (inbound > 0) {
    // Legs end here and nothing goes onward. A real record, and it stands.
    return { ok: true, kind: 'drop_only', reason: null };
  }
  if (taking > 0) {
    // THE LEFTOVER PICKUP. No leg to close; the pile was already here.
    return { ok: true, kind: 'pickup', reason: null };
  }
  return {
    ok: false, kind: 'nothing',
    reason: 'nothing is on its way here and nothing is being taken',
  };
}

// Who took custody at a handover — a real user, or the "someone else" escape.
// The two can never both be set; the database refuses it.
export function receivedBy(load, usersById = {}) {
  if (load?.handed_to_user) {
    const u = usersById[String(load.handed_to_user)];
    return u?.full_name || u?.name || u?.email || 'a colleague';
  }
  return load?.handed_to_name || null;
}

// ── WHAT IS STILL AT A HANDOVER, ITEM BY ITEM ───────────────────────────────
//
// #529 REVISED, Session 79.10, after Colby: "houston may leave 200 frames at
// the hub for the handover, but the dallas driver may only be able to load and
// bring 100 frames back with him that day. the dallas driver has to have a way
// to select how many he's bringing back and how many he's leaving so he can
// pick them up on another day."
//
// ** THE FIRST BUILD OF THIS TOOK EVERYTHING OR NOTHING. ** He had already
// asked for exactly this -- "partial fufillments on both the shipping and
// receiving trucks" -- and the receiving half shipped all-or-nothing anyway.
//
// A HANDOVER IS A PLACE, NOT A RECORD, and that is the whole of the fix. The
// question a driver answers is how many of a thing are standing at Austin for
// this request: what has been dropped there, minus what has already been taken
// away. Two Houston loads at one yard are ONE PILE to the person loading a
// truck, and the database already agrees -- load_branch_transfer() checks the
// total standing at the branch it is given, never a per-load figure. Building
// this per-load would have let a driver take 100 from each of two cards when
// only 150 were there.
//
//   standing   dropped here and not yet taken away. Physically on the ground.
//   rolling    on a truck addressed here, not yet arrived. Being met.
//   available  the two together -- what a driver standing there could load.
//
// #629, Session 79.22b -- A STOP IS A BRANCH OR A PLACE ON THE ROAD. Gate 553
// D10 lets a driver hand over "on the road" at a place he types, and the WRITE
// side always supported it: the load records `to_branch_id: null` and
// `to_place: 'I-45 at Buffalo'`, and hand_over_load() already takes a place.
// Every READ here asked which BRANCH material was waiting at. A road spot has
// no branch, so ten stakes left Houston's books, waited nowhere, and no screen
// could collect them or write them off. `hub` may now be a branch id, as every
// caller has always passed, or a stop `{ branchId, place }` from stopOf().
export function stopOf(load) {
  if (!load) return null;
  if (load.to_branch_id != null) return { branchId: load.to_branch_id, place: null };
  const place = load.to_place ? String(load.to_place).trim() : '';
  return place ? { branchId: null, place } : null;
}

// One key per stop, so a branch id and a place can never collide.
export function stopKey(stop) {
  if (!stop) return null;
  if (stop.place) return `place:${stop.place}`;
  return stop.branchId != null ? `branch:${stop.branchId}` : null;
}

function asStop(hub) {
  if (hub && typeof hub === 'object') {
    const place = hub.place ? String(hub.place).trim() : '';
    return place ? { branchId: null, place } : { branchId: hub.branchId ?? null, place: null };
  }
  return { branchId: hub ?? null, place: null };
}

// A place matches only a load with no branch at that end, so a branch and a
// place typed with the same words can never be confused. A stop naming
// neither matches nothing -- it used to match every road load at once, because
// String(null) === String(null).
function endsAt(load, stop) {
  if (stop.place) return load?.to_branch_id == null && String(load?.to_place || '').trim() === stop.place;
  return stop.branchId != null && String(load?.to_branch_id) === String(stop.branchId);
}
function startsAt(load, stop) {
  if (stop.place) return load?.from_branch_id == null && String(load?.from_place || '').trim() === stop.place;
  return stop.branchId != null && String(load?.from_branch_id) === String(stop.branchId);
}

export function waitingAtHub(loads, transfer, hub) {
  const stop = asStop(hub);
  const all = Array.isArray(loads) ? loads : [];
  const tid = transfer?.id ?? null;
  const mine = all.filter(l => {
    // Scope to this transfer when BOTH sides know which one it is. A fixture
    // or a detail panel that already holds one transfer's loads says nothing,
    // and filtering an unknown against an unknown is how a list becomes empty.
    if (tid != null && l?.transfer_id != null && String(l.transfer_id) !== String(tid)) return false;
    return l?.status === 'in_transit' || l?.status === 'arrived';
  });

  const rows = new Map();
  const inTransit = [];
  const refs = new Set();
  // #570 H13 — SINCE WHEN. The standing line reads "50 frames still standing
  // at Austin since Sep 11, 11:20 am", and the moment it means is when the
  // material was PUT DOWN here: the earliest arrival among the legs that ended
  // at this hub. A later leg arriving does not restart the clock on a pile
  // that has been sitting since Tuesday - which is the sentence D36 asked for
  // when it wanted "at Austin since Tuesday" instead of "in transit".
  let since = null;

  for (const l of mine) {
    const toHub = endsAt(l, stop);
    const fromHub = startsAt(l, stop);
    if (!toHub && !fromHub) continue;
    if (toHub && l.status === 'in_transit') inTransit.push(l);
    if (toHub && l.load_ref) refs.add(l.load_ref);
    if (toHub && l.status === 'arrived' && l.arrived_at) {
      const t = stamp(l.arrived_at);
      if (t && (since == null || t < since)) since = t;
    }

    for (const li of (l.branch_transfer_load_items || l.items || [])) {
      const k = String(li.item_id);
      if (!rows.has(k)) {
        rows.set(k, {
          item_id: li.item_id,
          name: li.inventory_items?.name || null,
          uom: li.inventory_items?.uom || null,
          // Carried forward so the onward leg travels at the cost it arrived
          // at rather than at nothing -- 79.6 lost a whole shipment's value to
          // exactly this, twice, on one field.
          unit_cost: li.unit_cost_at_transfer ?? null,
          sourced: li.sourced ?? null,
          standing: 0, rolling: 0,
        });
      }
      const r = rows.get(k);
      if (r.unit_cost == null && li.unit_cost_at_transfer != null) r.unit_cost = li.unit_cost_at_transfer;
      if (r.sourced == null && li.sourced != null) r.sourced = li.sourced;

      // Arrived here. Only a counted load has landed; one still on the road
      // carries a null qty_arrived and has not.
      if (toHub && l.status === 'arrived') r.standing += num(li.qty_arrived);
      if (toHub && l.status === 'in_transit') r.rolling += num(li.qty_loaded);
      // Left here, whether or not it has got where it is going yet.
      if (fromHub) r.standing -= num(li.qty_loaded);
    }
  }

  const out = [...rows.values()]
    .map(r => {
      const standing = r.standing > 0 ? r.standing : 0;
      return { ...r, standing, available: standing + r.rolling };
    })
    .filter(r => r.available > 0);

  return {
    branchId: stop.branchId,
    place: stop.place,
    rows: out,
    loads: inTransit,
    loadRefs: [...refs],
    total: out.reduce((n, r) => n + r.available, 0),
    standing: out.reduce((n, r) => n + r.standing, 0),
    rolling: out.reduce((n, r) => n + r.rolling, 0),
    // H13. Null when nothing has actually landed here yet - a pile that is
    // still on a truck has not been standing anywhere.
    standingSince: since,
  };
}

// #709, Session 79.22b.4.3 -- WHAT IS ACTUALLY STILL ON ITS WAY, AND WHERE.
//
// The transfer sheet said "{shipped - received} still in transit". That number
// cannot tell a unit on a moving truck from one that fell off it: a truck of 5
// counted in as 3 read "2 still in transit" while the history, three lines
// down, said the only truck had arrived. The mirror of #674.
//
// Read from the trucks instead: what is loaded on one still on the road, by
// where it is heading, and what stands set down at a pickup point waiting to be
// fetched. Nothing moving and nothing waiting says nothing -- the units that are
// simply missing are the write-off's to name, not this line's.
export function stillOnItsWaySentence(loads, transfer, branchNames = {}) {
  const all = Array.isArray(loads) ? loads : [];
  const placeName = (stop) => (stop?.branchId != null
    ? branchNames[String(stop.branchId)] || 'another branch'
    : stop?.place || 'a handover on the road');
  const moving = new Map();
  for (const l of all) {
    if (l?.status !== 'in_transit') continue;
    const qty = (l.branch_transfer_load_items || l.items || []).reduce((n, li) => n + num(li.qty_loaded), 0);
    if (qty <= 0) continue;
    const name = placeName(stopOf(l));
    moving.set(name, (moving.get(name) || 0) + qty);
  }
  const parts = [...moving].map(([name, n]) => `${n} still in transit to ${name}`);
  for (const hub of hubsWaitingFor(all, transfer)) {
    if (hub.standing > 0) parts.push(`${hub.standing} waiting at ${placeName(hub)}`);
  }
  return parts.length ? parts.join(' · ') : null;
}

// Every place on this transfer holding something for its destination -- a
// branch acting as a hub, or (#629) a spot on the road, which used to be
// filtered out here by `to_branch_id != null` and was therefore waiting nowhere.
export function hubsWaitingFor(loads, transfer) {
  const all = Array.isArray(loads) ? loads : [];
  const dest = transfer?.to_branch_id ?? null;
  const stops = new Map();
  for (const l of all) {
    if (!(l?.status === 'in_transit' || l?.status === 'arrived')) continue;
    const stop = stopOf(l);
    if (!stop) continue;
    // The destination is where a transfer ENDS, never a stop along the way.
    if (stop.branchId != null && String(stop.branchId) === String(dest)) continue;
    const k = stopKey(stop);
    if (!stops.has(k)) stops.set(k, stop);
  }
  return [...stops.values()].map(s => waitingAtHub(all, transfer, s)).filter(s => s.total > 0);
}

// ONE CARD PER PILE, NOT ONE PER LOAD. Two Houston drops at Austin are one trip
// for the driver, and counting them twice would inflate the tab's badge and
// offer the same job in two places.
//
// The `arrived` clause is the partial collection: a leg that ended at Austin
// with material still standing there is still somebody's job.
export function toCollectFor(loads, branchId) {
  const all = Array.isArray(loads) ? loads : [];
  const seen = new Set();
  const out = [];
  for (const l of all) {
    if (l?.status !== 'in_transit' && l?.status !== 'arrived') continue;
    if (String(finalStop(l)) !== String(branchId)) continue;
    if (String(l.to_branch_id) === String(branchId)) continue;
    // #629 -- keyed on the STOP, a branch or a place on the road. Keyed on
    // `to_branch_id` alone, every road load read as "branch null" and was
    // counted against every other road load of the same transfer.
    const stop = stopOf(l);
    if (!stop) continue;
    const key = `${l.transfer_id ?? '?'}:${stopKey(stop)}`;
    if (seen.has(key)) continue;
    if (waitingAtHub(all, { id: l.transfer_id ?? null }, stop).total <= 0) continue;
    seen.add(key);
    out.push(l);
  }
  return out;
}

export function movingElsewhere(loads, branchId) {
  return (Array.isArray(loads) ? loads : []).filter(l =>
    l.status === 'in_transit' &&
    String(finalStop(l)) !== String(branchId) &&
    String(l.to_branch_id) !== String(branchId));
}

// ── THE FOURTH QUESTION: WHAT IS STILL OWED TO ME, WITH NOTHING MOVING ──────
//
// #531, Session 79.12. The three questions above all need a LOAD — a truck on
// the road, or a pile standing at a yard. This is the case where there is
// neither, and that is precisely why it had no tab: Houston has sent some or
// all of what Dallas asked for, every truck has stopped, and the request is
// still open.
//
// COLBY, 79.11: a part-sent transfer with nothing on the road "is in no tab at
// all for the branch waiting on it". Two live examples on staging when it was
// found — 68 of 70 received and 148 of 150 — and the only control that writes
// the balance off lives on a document no tab could reach.
//
// TWO KINDS, AND THEY ARE TWO DIFFERENT EVENTS RATHER THAN TWO SHADES OF ONE.
// Colby's own reading at 79.12, and the split decides the colour on the card,
// the words on the chip, and which close applies:
//
//   'awaiting'  Houston has not sent it all. Houston still sees it in To load,
//               because isLoadable() is the same test — so nobody is confused
//               about whose move it is, and nothing has been lost. Dallas is
//               waiting. The close is close_branch_transfer_unfilled,
//               "nothing more is coming", and it reports no money.
//
//   'short'     Everything asked for went on a truck and less was counted off
//               it. A DISCREPANCY: the gap opened between what somebody at
//               Houston said was loaded and what Dallas counted in — and with
//               Austin in the route there are TWO handovers it could have
//               opened at, which is why the per-load MISSING/EXTRA record
//               (D63) is where the evidence lives rather than here. The close
//               is close_branch_transfer_short, which reports the DOLLAR VALUE
//               and pages both branches' managers and every admin with no
//               preference toggle.
//
// ** THE TWO EXCLUSIONS ARE THE WHOLE CORRECTNESS OF THIS FUNCTION. **
//
// #531's approved rule was "not finished where received < shipped". That
// OVER-FIRES, and the reason is the hub: arriving at Austin deliberately does
// not count as received, so material standing at a yard and material on a
// truck right now BOTH satisfy it. Either would be reported as a discrepancy
// while being perfectly healthy — and would appear in two tabs at once, which
// is the exact thing the seven-stage rebuild exists to prevent. So nothing may
// be rolling and nothing may be standing before a gap means the goods are
// gone.
//
// KEYED ON THE BRANCH RATHER THAN ON seesEveryBranch, because that is what the
// other three questions do — "On the way" has always been a branch-keyed tab.
// The one overlap this leaves is deliberate and is written down rather than
// left to look like an oversight: an admin or executive sees every branch's To
// load, so for them an 'awaiting' row shows there as somebody's job AND here
// as their own branch's wait. Two different questions for a person wearing two
// hats, and 79.6's lesson was that hiding a branch's work from an admin is the
// worse error.
export function owedToBranch(transfers, loads, branchId) {
  if (branchId == null) return [];
  const all = Array.isArray(loads) ? loads : [];
  const out = [];
  for (const t of (Array.isArray(transfers) ? transfers : [])) {
    if (!t || String(t.to_branch_id) !== String(branchId)) continue;
    // #580 part a -- made for this branch's own shelf: nothing is on its way
    // from anywhere, so it is never "awaited" here (gate 553 X1 line 4).
    if (isOwnShelf(t)) continue;
    // Same live window as isLoadable/isUnfillable. Anything earlier is still
    // being agreed and already shows in Awaiting confirmation; anything later
    // is Finished.
    if (!['approved', 'in_transit'].includes(t.status)) continue;

    const its = all.filter(l => String(l.transfer_id) === String(t.id));
    if (its.some(l => l?.status === 'in_transit')) continue;
    if (hubsWaitingFor(its, t).length > 0) continue;

    const progress = transferProgress(t.branch_transfer_items || t.items || []);
    if (progress.requested <= 0) continue;

    if (progress.shipped < progress.requested) {
      out.push({
        transfer: t, kind: 'awaiting', progress,
        missing: progress.requested - progress.shipped,
      });
    } else if (progress.received < progress.shipped && awaitingCountUnits(its, t) === 0) {
      // #674 -- the card's "never arrived" had the sheet's fault: a load in
      // and waiting to be counted is in the count-in queue, not short.
      out.push({
        transfer: t, kind: 'short', progress,
        missing: progress.shipped - progress.received,
      });
    }
  }
  // A discrepancy is somebody's move today; a wait is not. Oldest first inside
  // each kind, so what has sat longest is at the top rather than whatever was
  // created last.
  const rank = k => (k === 'short' ? 0 : 1);
  return out.sort((a, b) =>
    rank(a.kind) - rank(b.kind) ||
    new Date(a.transfer.requested_at || 0) - new Date(b.transfer.requested_at || 0));
}

// #676, Session 79.22b.4 -- THE SENDING BRANCH'S SHORT TRANSFERS. #531 part 2
// gave a branch manager at EITHER end the right to write off the loss, on the
// reasoning that the sender is the one who can see the rack the material is
// not on. Then no tab listed it for the sender: Houston sent 30, Dallas
// counted 28, and Houston's manager looked through all seven tabs and found
// the transfer on none of them -- so the right existed and had no route. This
// is owedToBranch()'s short case seen from the other end: sent, short, nothing
// still moving, nothing waiting to be counted. Same window and same shape, so
// the On the way tab can list both with one card.
export function shortFromBranch(transfers, loads, branchId) {
  if (branchId == null) return [];
  const all = Array.isArray(loads) ? loads : [];
  const out = [];
  for (const t of (Array.isArray(transfers) ? transfers : [])) {
    if (!t || String(t.from_branch_id) !== String(branchId)) continue;
    if (isOwnShelf(t)) continue;
    if (!['approved', 'in_transit'].includes(t.status)) continue;
    const its = all.filter(l => String(l.transfer_id) === String(t.id));
    if (its.some(l => l?.status === 'in_transit')) continue;
    if (hubsWaitingFor(its, t).length > 0) continue;
    if (awaitingCountUnits(its, t) > 0) continue;
    const progress = transferProgress(t.branch_transfer_items || t.items || []);
    if (progress.shipped <= 0 || progress.shipped < progress.requested) continue;
    if (progress.received < progress.shipped) {
      out.push({ transfer: t, kind: 'short', progress, missing: progress.shipped - progress.received, sent: true });
    }
  }
  return out.sort((a, b) => new Date(a.transfer.requested_at || 0) - new Date(b.transfer.requested_at || 0));
}

// ── WHAT IS ON ITS WAY TO A BRANCH, PER ITEM — #582 and gate 553 D25 ────────
//
// Gate 371 D19's promise, never built until 79.21b: a low-stock row with stock
// on the way says so while its status still reads Out or Low, because a job
// today cannot be cut from vinyl on a truck and a second order spends real
// money. ONE derivation, from the same per-line arithmetic the Transfers screen
// uses (lineProgress over agreedLines), so the row and the transfer can never
// disagree — and #512's Item Detail line reads this later rather than a copy.
//
// Three kinds, keyed by item id:
//   arriving    another branch is sending it: approved or on the road, what has
//               not yet been counted in (refused items never count, #577)
//   made_here   our own shop is making it for our shelf and has confirmed a
//               date: what is still to make (runs already signed off are on
//               the shelf, so they are not "coming")
//   asked_here  asked of our own shop, not yet confirmed — not a commitment,
//               so it is said but never counted as covering the shortfall
// A request from another branch that is still being agreed is none of these:
// nothing has been promised.
//
// ON_ITS_WAY_STATUSES -- the transfer statuses a reader fetches for this
// derivation. Lifted here at #512 (Session 79.22b.6.1f) from Stock Home's own
// query, unchanged, so Stock Home and the item page ask for the same set and
// cannot drift into disagreeing about what is coming. closed_unfilled stays in
// for #600: a truck already out when the rest was stopped still arrives.
export const ON_ITS_WAY_STATUSES = ['awaiting_confirmation', 'approved', 'in_transit', 'closed_unfilled'];

export function onItsWayTo(transfers, branchId) {
  const out = {};
  if (branchId == null) return out;
  const add = (itemId, entry) => { (out[itemId] = out[itemId] || []).push(entry); };
  for (const t of (Array.isArray(transfers) ? transfers : [])) {
    if (!t || String(t.to_branch_id) !== String(branchId)) continue;
    const own = isOwnShelf(t);
    for (const l of agreedLines(t.branch_transfer_items || t.items || [])) {
      if (l?.item_id == null) continue;
      const p = lineProgress(l);
      const promised = promisedDateFor(l, t);
      if (own) {
        if (t.status === 'awaiting_confirmation' && p.requested > 0) {
          add(l.item_id, { kind: 'asked_here', qty: p.requested, transfer: t });
        } else if (t.status === 'approved' && p.toMake > 0) {
          add(l.item_id, { kind: 'made_here', qty: p.toMake, promised, transfer: t });
        }
        continue;
      }
      // #600 -- ENDED "NOTHING MORE COMING", BUT A TRUCK MAY STILL BE OUT.
      // Nothing more will be sent (371 D33), so the balance is not coming; what
      // is already on a truck, or standing at a hub for us, is -- and that is
      // the number the row shows. No promised date: the promise was for the rest.
      if (t.status === 'closed_unfilled') {
        if (p.inTransit > 0) {
          add(l.item_id, { kind: 'arriving', qty: p.inTransit, fromBranchId: t.from_branch_id, promised: null, transfer: t });
        }
        continue;
      }
      if (!['approved', 'in_transit'].includes(t.status)) continue;
      if (p.outstanding > 0) {
        add(l.item_id, { kind: 'arriving', qty: p.outstanding, fromBranchId: t.from_branch_id, promised, transfer: t });
      }
    }
  }
  return out;
}

// What is PROMISED — the part that covers a shortfall. An unconfirmed ask is not.
export function committedQty(entries) {
  return (Array.isArray(entries) ? entries : [])
    .filter(e => e.kind !== 'asked_here')
    .reduce((sum, e) => sum + (Number(e.qty) || 0), 0);
}

// Covered: what is promised brings the shelf back to its reorder point. With no
// reorder point the row is on the list only because it is Out, and anything
// promised covers that.
export function isCovered(row, entries) {
  const coming = committedQty(entries);
  if (coming <= 0) return false;
  const qty = Number(row?.qty_on_hand) || 0;
  if (row?.reorder_point == null) return qty + coming > 0;
  return qty + coming >= Number(row.reorder_point);
}

// "Fri" within the coming week, "Oct 2" beyond it or in the past — gate 553
// draws "promised Fri" and a date further out needs its month.
export function shortPromisedDay(value, now = new Date()) {
  if (!value) return null;
  const d = new Date(String(value).length === 10 ? `${value}T12:00:00` : value);
  if (Number.isNaN(d.getTime())) return null;
  const startOf = x => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(d) - startOf(now)) / 86400000);
  if (days >= 0 && days <= 6) return d.toLocaleDateString('en-US', { weekday: 'short' });
  return dateList(d);
}

// The row's own words for one entry — gate 553 F2 and gate 371 D19's drawing.
//
// `elsewhereName` is set when the person reading is NOT at the row's branch --
// a manager looking at another branch's Stock Home -- so "here" becomes the
// branch's name rather than a word that is false for them.
export function onItsWayLine(entry, { branchNames = {}, now = new Date(), elsewhereName = null } = {}) {
  if (!entry) return null;
  const n = Number(entry.qty) || 0;
  if (entry.kind === 'asked_here') return `${n} asked for · waiting for a date`;
  // #667, Session 79.22b.4 -- A LINE WITH NO DATE SAYS SO. Colby: "if no date
  // is selected it needs to say something telling the user that so they dont
  // wonder why one line is dated and the other isnt." Two lots from Houston read
  // "10 arriving from Houston" and "5 arriving from Houston, Oct 5", and a blank
  // cannot be told from a screen that failed to show a date -- #573's principle,
  // one screen over. "given" rather than "yet": a truck still out after the rest
  // was stopped carries no date and never will (#600), and "yet" would promise
  // one. The date stays optional at confirm; only the silence is named.
  if (entry.kind === 'made_here') {
    const day = shortPromisedDay(entry.promised, now);
    const where = elsewhereName ? `at ${elsewhereName}` : 'here';
    return day ? `${n} being made ${where} · promised ${day}` : `${n} being made ${where} · no date given`;
  }
  if (entry.kind === 'arriving') {
    const from = branchNames[String(entry.fromBranchId)] || 'another branch';
    return entry.promised
      ? `${n} arriving from ${from}, ${dateList(entry.promised)}`
      : `${n} arriving from ${from} · no date given`;
  }
  return null;
}

// NAMES FOR WHOEVER APPEARS IN A HISTORY — #553 D20.
//
// Deliberately NOT the branch driver list: a journey crosses branches, so the
// person who loaded at Houston, the one who took it on at Austin and the one
// who counted it in at Dallas are three people at three branches. Fetched by
// the ids actually present rather than by pulling `users`, so the query is
// bounded by the transfer rather than by the company.
export async function fetchUserNames(ids) {
  const want = [...new Set((Array.isArray(ids) ? ids : []).filter(Boolean).map(String))];
  if (want.length === 0) return {};
  const { data, error } = await supabase
    .from('users').select('id, full_name').in('id', want);
  // An empty map renders as "not recorded", which is honest. Inventing a name
  // is the one outcome B6 rules out.
  if (error) return {};
  return Object.fromEntries((data || []).map(u => [String(u.id), u]));
}

// THE IDS A HISTORY WILL NEED, read off the loads so the caller does not have
// to know which columns hold people.
export function peopleInLoads(loads) {
  const out = [];
  for (const l of (Array.isArray(loads) ? loads : [])) {
    // `undone_by` since #694 (79.22b.4.6.1): the Undo's line names its person.
    out.push(l?.loaded_by, l?.arrived_by, l?.counted_by, l?.handed_to_user, l?.driver_id, l?.undone_by);
  }
  return [...new Set(out.filter(Boolean).map(String))];
}

// ── #570 H3 — THE PEOPLE ON THE TRANSFER ITSELF ─────────────────────────────
//
// FOUND BY LOOKING AT THE SCREEN, not by reading the code. H3 put the
// transfer's own events into the history and every one of them rendered
// "not recorded", because the only people ever fetched came from
// peopleInLoads() - and a transfer with no loads has none. So the history
// gained the lines it was built for and could not name anybody on them.
//
// That is D37's OWN acceptance criterion failing: "every line names a person
// and a time". The gate said what good looked like and the first build of it
// did not meet the gate.
//
// Kept SEPARATE from peopleInLoads() rather than folded into it: that function
// answers "who touched a truck", which several callers rely on, and quietly
// widening it would change what they mean.
export function peopleInTransfer(transfer) {
  if (!transfer) return [];
  // `denied_by` joined at #573 (Session 79.21): the refusal line names its
  // person, and a name that was never fetched prints "not recorded" - the
  // exact fault this function was added to stop.
  // `cancelled_by` and `refusal_decided_by` joined at 79.21b (#590, #578):
  // a withdrawal and a routing decision both name their person now.
  const out = [
    transfer.requested_by, transfer.endorsed_by, transfer.approved_by,
    transfer.shipped_by, transfer.received_by, transfer.closed_by,
    transfer.denied_by, transfer.cancelled_by, transfer.refusal_decided_by,
  ];
  return [...new Set(out.filter(Boolean).map(String))];
}

// ── WHICH BRANCH REFUSED — #572, and ONE copy of it since #573 ────────────────
//
// A transfer can be refused at either stage, and the two refusals come from
// different branches. Stage 1 is the ASKING branch's own manager turning their
// team member down; stage 2 is the SUPPLYING branch saying it cannot send.
//
// `endorsed_at` is the only thing on the row that tells them apart: it is set
// when the asking branch approves, so a refusal on a transfer that carries it
// came after that approval - from the supplier. #572 exists because every
// refusal used to name the supplier, putting a Dallas manager's own words in
// Houston's mouth.
//
// It lived inline in the transfer detail until the history needed the same
// answer. Two copies of one rule drift in exactly one of them, so both now ask
// here.
export function refusingBranchId(transfer) {
  if (!transfer) return null;
  return transfer.endorsed_at ? transfer.from_branch_id : transfer.to_branch_id;
}

// ── WHICH BRANCH REFUSED THIS ITEM — #698 half (b), Session 79.22b.4.7 ───────
//
// refusingBranchId() is one fact for a whole transfer, and it stops being true
// once the asking branch can turn down SOME items at the first stage and send
// the rest on (gate 665 D26): one transfer then carries refusals from both
// branches. So each refused item records its own
// (branch_transfer_items.refused_by_branch_id, written by the Deny rule and by
// the supplier's item-by-item answer - 20260927_refusal_per_item_698.sql), and
// this reads it.
//
// A row read without the field falls back to the transfer's rule, which every
// refusal made before 79.22b.4.7.1 matches: the migration filled them in by it.
// refusingBranchId() still answers for the WHOLE transfer's refusal - the
// history's "Refused by" line and the detail header - because a denial of the
// whole transfer is one branch's by definition.
export function lineRefusedByBranchId(line, transfer) {
  if (!line || line.line_status !== 'refused') return null;
  if (line.refused_by_branch_id != null) return line.refused_by_branch_id;
  return refusingBranchId(transfer);
}

// #571's rule per item: only what the SUPPLYING branch refused comes home to
// the asking manager to route - an item the asking branch turned down itself
// routes nowhere, because its own manager decided it is not needed. A request
// for a branch's own shelf is refused by that one branch, so every refusal on
// it counts here, exactly as before; the screen's own stage guard is what
// keeps its routing panel away (#627, left for 79.23a).
export function refusedBySupplier(line, transfer) {
  const b = lineRefusedByBranchId(line, transfer);
  return b != null && transfer != null && String(b) === String(transfer.from_branch_id);
}

// The items the routing panel offers, the same set route_denied_transfer()
// accepts. One copy for the sheet and the history, so they cannot disagree.
export function routableRefusedLines(lines, transfer) {
  return (Array.isArray(lines) ? lines : []).filter(l => refusedBySupplier(l, transfer));
}

// ── #753 / #752, Session 79.22b.5.4b -- ONE RULE FOR "THIS REFUSAL STILL WAITS
// FOR A DECISION" ──────────────────────────────────────────────────────────────
//
// Read by the transfer sheet's routing panel and by Finished's "Waiting for
// {branch} to decide" group, so the two cannot disagree. Four questions:
//   - the SUPPLYING branch refused at least one item (#571: what the asking
//     branch turned down itself routes nowhere; a refusal before the asking
//     branch endorsed it is that branch's own decision);
//   - nobody has decided (refusal_decision, or an older routing that left only
//     a successor document - withSuccessors() marks those);
//   - and the transfer was NOT WITHDRAWN. #753: a withdrawn transfer kept
//     saying "needs your decision" and offered three routes the database
//     refuses (route_after_withdraw=REFUSED, 79.21b's proof).
// Whether THIS PERSON may decide is a separate question - canRouteRefusedLines().
export function refusalAwaitsDecision(transfer) {
  if (!transfer) return false;
  if (transfer.status === 'cancelled') return false;
  if (!transfer.endorsed_at) return false;
  if (transfer.refusal_decision || transfer.__hasSuccessor) return false;
  return routableRefusedLines(transfer.branch_transfer_items, transfer).length > 0;
}

// ── NOT NEEDED — #698 half (b), Session 79.22b.4.7.1, gate 665 D26 ──────────
//
// An item the ASKING branch turned down itself, at the first stage: "Only some
// of these are needed". Every screen says it was not needed, as the asking
// branch's own decision - never the supplier's (#572) - and the supplying
// branch is never asked about it (gate 665 X2). A request for a branch's own
// shelf has one branch at both ends and no first stage, so nothing on it is
// ever this; its refusals stay the supplier's, exactly as before (#627).
export function refusedByAsker(line, transfer) {
  if (!transfer || String(transfer.from_branch_id) === String(transfer.to_branch_id)) return false;
  const b = lineRefusedByBranchId(line, transfer);
  return b != null && String(b) === String(transfer.to_branch_id);
}

// THE ITEMS THE SUPPLYING BRANCH IS ASKED ABOUT: everything but what the asking
// branch turned down. Its two approval cards (the Requests inbox and Awaiting
// confirmation), both item-by-item forms, their counts and the one-item rule
// all read this list - #698 finding 5, Colby's ruling A at 79.22b.4.7.1's open.
// Where the transfer is a RECORD (the sheet's item table, the history, the work
// cards) the item stays, reading "not needed".
export function linesPutToSupplier(lines, transfer) {
  return (Array.isArray(lines) ? lines : []).filter(l => !refusedByAsker(l, transfer));
}

// ── THE TRANSFER HISTORY — #553 D20, and #515's D37 arriving at last ─────────
//
// FOUR COLUMNS, AND THE SHAPE IS BINDING ON THE BUILD. Colby, reading the
// mockup, unprompted: "i like this table layout in the transfer details, it's
// clean and easy to read and i'd like to preserve this structure in the app."
//
//   WHEN   the time it happened
//   WHAT   what happened, with the quantity
//   WHO    the person, and an ARROW on a change of custody
//   WHERE  the branch or the spot on the road
//
// It is recorded as a DECISION rather than a preference because it is what
// makes D37's H2 checkable: "every line names a person and a time" is
// satisfiable or not by looking at whether those two columns are ever empty.
//
// ** ONE ROW PER EVENT, NOT PER LOAD. ** The old list printed one line per load
// and folded four different happenings into it, so a leg that was loaded on
// Monday, handed over on Tuesday and counted in on Thursday read as a single
// line with one date. Three things happened to that material and three people
// touched it.
//
// ── THE COLUMN THIS MUST NOT BE BUILT ON, AND B6 SAYS SO OUTRIGHT ──
//
// `driver_id` HAS NOT ALWAYS HELD A DRIVER. Before D12 it was written as
// auth.uid() -- the person who pressed Load -- while the actual driver's name
// sat in a free-text box attached to no account. B6: "a history built on
// today's driver_id would print a name confidently and wrongly", and "a wrong
// name in an audit trail is worse than no name."
//
// SO THE RULE HERE IS NARROW ON PURPOSE: the person who pressed the button is
// named as having RECORDED the event, and a driver is named separately ONLY
// when we can actually tell them apart -- a typed `driver_name`, or a
// `driver_id` that differs from whoever pressed it. When the two are the same
// id the name is printed ONCE and no claim is made about who drove, which is
// true both for a legacy row and for D12's default of the presser driving.
// Old legs are never retro-assigned; they simply render as recorded by.

const stamp = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d;
};

// WHO, resolved to something printable, and null rather than a guess.
function personName(id, usersById) {
  if (id == null) return null;
  const u = usersById ? usersById[String(id)] : null;
  return u?.full_name || u?.name || u?.email || null;
}

// #728, Session 79.22b.6e -- WHO HANDED A LOAD OVER, ONE RULE for the history
// and the handover bell. The leg's DRIVER, typed or recorded; never whoever
// pressed Send. With "Loaders load, drivers drive" on nobody is recorded as
// driving (#690), and the history used to fall back to the LOADER - "Owen Pike
// → Team Member · Austin" for a man who was never at Austin. The bell already
// said "the driver from Houston"; now both say it. With D12's default the
// presser IS the recorded driver, so that row reads exactly as before.
// `nameOf(id)` resolves an id to a printable name or null.
export function handoverGiver(leg, { nameOf = () => null, branchNames = {} } = {}) {
  return leg?.driver_name
    || (leg?.driver_id != null ? nameOf(leg.driver_id) : null)
    || `the driver from ${branchNames[String(leg?.from_branch_id)] || 'the other branch'}`;
}

// ── #570 H13 — THE STANDING SENTENCE ────────────────────────────────────────
//
// "50 frames still standing at Austin since Sep 11, 11:20 am".
//
// IT IS A PURE FUNCTION RATHER THAN JSX because the wording IS the deliverable
// and the gate specifies it to the word. Left inside the component it could
// only be checked by rendering a screen, and nothing is standing at a hub on
// staging today - so it would have shipped unverified.
//
// ONE ITEM NAMES ITSELF; several are counted rather than listed, because this
// line is a pointer to the To collect tab, not a manifest.
export function standingSentence(stop, branchNames = {}, items = {}) {
  if (!stop || !(stop.standing > 0)) return null;
  // #629 -- a road stop names the place the driver typed; a branch its name.
  const place = stop.place || branchNames[String(stop.branchId)] || 'the hub';
  const named = (stop.rows || []).filter(r => r.standing > 0);
  const what = named.length === 1
    ? (named[0].name || items[String(named[0].item_id)]?.name || 'items')
    : 'items';
  const since = stop.standingSince
    ? ` since ${stop.standingSince.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`
      + `, ${stop.standingSince.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`
    : '';
  return `${stop.standing} ${what} still standing at ${place}${since}`;
}

// WHAT AN EVENT READS AS, composed from its kind and quantity rather than
// baked in at the point it is pushed.
//
// H12 is why: a single hub collection ends SEVERAL inbound legs in one action
// by one person, and the gate says that is ONE line naming the loads it ended -
// "rendering it as one line per load would invent events that did not happen
// and put the same name and the same minute on each". Merging two lines means
// summing their quantities and re-rendering the label, which cannot be done
// once the number is inside a sentence.
//
// H14 is the other reason: the reconciliation test adds up `qty` by `kind`. If
// it had to parse these strings, the first wording change would break it.
// #603, Session 79.21b -- THE RUNS THAT MADE THE GOODS, read for the history.
// `branch_transfer_production` has recorded how many, who, and when since Sep
// 14 and NOTHING in the app read it, so a transfer made in three runs over four
// days showed one "Loaded 100" line and no trace of the making. The rows hang
// off the transfer's LINES rather than the transfer, so the line ids are read
// first -- two plain reads rather than a filter through an embedded relation,
// which PostgREST answers with silent emptiness when it goes wrong.
export async function fetchProductionRuns(transferId) {
  if (transferId == null) return { data: [], error: null };
  const { data: lines, error: linesErr } = await supabase
    .from('branch_transfer_items').select('id, item_id').eq('transfer_id', transferId);
  if (linesErr) return { data: [], error: linesErr };
  const ids = (lines || []).map(l => l.id).filter(id => id != null);
  if (ids.length === 0) return { data: [], error: null };
  const { data, error } = await supabase
    .from('branch_transfer_production').select('*').in('transfer_item_id', ids);
  if (error) return { data: [], error };
  const itemOf = Object.fromEntries((lines || []).map(l => [String(l.id), l.item_id]));
  return { data: (data || []).map(r => ({ ...r, item_id: itemOf[String(r.transfer_item_id)] ?? null })), error: null };
}

// #712, Session 79.22b.4.3.1 -- HOW MANY WERE MADE, per transfer line: the sum
// of its production runs. An Undo is a negative run naming the one it took
// back, so the sum stays true. NOT `qty_ready`, which means "ready for this
// order" and since #628's shelf route also counts goods sent off the shelf --
// HOU-TRF-00040 sent 30 from Houston's shelf with no run at all and reads
// qty_ready 30 (#713). Keyed by the line's id, because a run hangs off a line.
export function madeByLine(runs) {
  const out = {};
  for (const r of (Array.isArray(runs) ? runs : [])) {
    if (r?.transfer_item_id == null) continue;
    const k = String(r.transfer_item_id);
    out[k] = (out[k] || 0) + num(r.qty);
  }
  return out;
}

// #713, 682 D11, Session 79.22b.6c.3 -- THE RUNS FOR EVERY LINE TO MAKE SHOWS,
// in one read rather than one per transfer. Only lines with something ready can
// have a run; a line with nothing ready made none, so it needs no read. ASKED
// WITH ITS COUNT (#608, readLimit.js): a list cut at the database's row limit
// would undercount what was made and call real production "from the shelf", so
// a cut or failed read returns nothing and the words read as before.
export async function fetchMadeByLine(lines) {
  const ids = [...new Set((Array.isArray(lines) ? lines : [])
    .filter(l => l?.id != null && num(l.qty_ready) > 0).map(l => l.id))];
  if (!ids.length) return { data: {}, error: null };
  const res = await supabase
    .from('branch_transfer_production')
    .select('transfer_item_id, qty', { count: 'exact' })
    .in('transfer_item_id', ids);
  if (res.error) return { data: null, error: res.error };
  if (cutOff(res)) return { data: null, error: null, cut: true };
  const sums = madeByLine(res.data);
  return { data: Object.fromEntries(ids.map(id => [String(id), sums[String(id)] || 0])), error: null };
}

// Each line carries `made_by_runs` (a screen-side field, never written back):
// its runs' sum where they were read, 0 where nothing is ready, and nothing at
// all where the read failed - so lineProgress() falls back to today's words.
export function withMadeCounts(transfers, madeMap) {
  return (Array.isArray(transfers) ? transfers : []).map(t => ({
    ...t,
    branch_transfer_items: (t?.branch_transfer_items || []).map(l => {
      if (num(l?.qty_ready) <= 0) return { ...l, made_by_runs: 0 };
      const m = madeMap ? madeMap[String(l.id)] : undefined;
      return m == null ? l : { ...l, made_by_runs: m };
    }),
  }));
}

// #712 -- WHAT LEFT THE MAKER'S SHELF rather than its production, per item, so
// a line's Made and Sent can be read together. FIRST legs only, leaving the
// maker: a collection's onward truck records its lines as `make` whatever the
// first truck carried (HOU-TRF-00036), and counting it would count goods twice.
// An undone truck carried nothing.
export function shelfSentByItem(loads, originBranchId) {
  const out = {};
  for (const l of (Array.isArray(loads) ? loads : [])) {
    if (!l || l.undone_at || l.prev_load_id != null) continue;
    if (String(l.from_branch_id) !== String(originBranchId)) continue;
    for (const i of (l.branch_transfer_load_items || [])) {
      if (i?.sourced_by !== 'stock') continue;
      const k = String(i.item_id);
      out[k] = (out[k] || 0) + num(i.qty_loaded);
    }
  }
  return out;
}

// The people on a run, so a name is fetched rather than printed as "not
// recorded" -- #594's lesson, which was exactly this shape one column over.
export function peopleInProduction(runs) {
  return [...new Set((Array.isArray(runs) ? runs : [])
    .map(r => r?.signed_off_by).filter(Boolean).map(String))];
}

// #694, Session 79.22b.4.6.1b -- EVERY COLLECTION ON A TRANSFER, each with what
// it closed (before and after) and the truck it opened. Read beside the loads,
// as the runs are, because a collection taken back is part of the journey and
// its truck no longer says so. A read that fails leaves the history without
// them rather than empty.
export async function fetchHandovers(transferId) {
  if (transferId == null) return { data: [], error: null };
  const { data, error } = await supabase
    .from('branch_transfer_handovers').select('*').eq('transfer_id', transferId)
    .order('id', { ascending: true });
  if (error) return { data: [], error };
  return { data: data || [], error: null };
}

// The people a collection's record names: who collected, who undid it, and
// who the trucks it closed were handed to.
export function peopleInHandovers(records) {
  const ids = [];
  for (const r of (Array.isArray(records) ? records : [])) {
    if (!r) continue;
    ids.push(r.created_by, r.undone_by);
    for (const leg of (Array.isArray(r.closed_legs) ? r.closed_legs : [])) {
      ids.push(leg?.after?.handed_to_user, leg?.after?.arrived_by);
    }
  }
  return [...new Set(ids.filter(Boolean).map(String))];
}

// #694, Session 79.22b.4.6.2 -- EVERY COUNT ON A TRANSFER, each with what its
// truck said after it, read beside the loads as the collections are: a count
// taken back is part of the journey, and the truck no longer says so itself.
// THE COLUMNS ARE NAMED, never '*': a record also holds each shelf's average
// cost before and after, which the history never shows and a team member is
// not shown (#530). A read that fails leaves the history without them.
const COUNT_IN_SELECT =
  'id, transfer_id, load_id, at_branch_id, at_destination, load_after, created_by, created_at, undone_at, undone_by';
export async function fetchCountIns(transferId) {
  if (transferId == null) return { data: [], error: null };
  const { data, error } = await supabase
    .from('branch_transfer_count_ins').select(COUNT_IN_SELECT).eq('transfer_id', transferId)
    .order('id', { ascending: true });
  if (error) return { data: [], error };
  return { data: data || [], error: null };
}

// The people a count's record names: who counted, and who undid it.
export function peopleInCountIns(records) {
  const ids = [];
  for (const r of (Array.isArray(records) ? records : [])) {
    if (!r) continue;
    ids.push(r.created_by, r.undone_by);
  }
  return [...new Set(ids.filter(Boolean).map(String))];
}

export function historyLabel(kind, qty, of = null) {
  const n = Number(qty) || 0;
  const sent = Number(of) || 0;
  switch (kind) {
    case 'loaded':          return n > 0 ? `Loaded ${n}` : 'Loaded';
    case 'handover':        return n > 0 ? `Handed over ${n}` : 'Handed over';
    case 'dropped':         return n > 0 ? `Left ${n} to be collected` : 'Left to be collected';
    case 'arrived_counted': return n > 0 ? `Arrived and counted in ${n}` : 'Arrived and counted in';
    case 'arrived':         return 'Arrived — not counted yet';
    case 'counted':         return n > 0 ? `Counted in ${n}` : 'Counted in';
    // #603. "Signed off" rather than "Made": it is the moment somebody SAID so,
    // which is what the history can honestly record.
    case 'signed_off':      return n > 0 ? `Signed off ${n}` : 'Signed off';
    // #694 -- the Undo of a sign-off, its own line beside the run it took back.
    case 'sign_off_undone': return n > 0 ? `Undid the sign-off of ${n}` : 'Undid a sign-off';
    // #694, 79.22b.4.6.1 -- the Undo of a Send, beside the load it took back.
    case 'send_undone':     return n > 0 ? `Undid the send of ${n}` : 'Undid a send';
    // #694, 79.22b.4.6.1b -- a collection taken back: its Undo, and the
    // handover it erased from the truck, kept from the collection's record.
    // Kinds of their own, so H12's merge re-renders them with the word.
    case 'collect_undone':  return n > 0 ? `Undid the collection of ${n}` : 'Undid a collection';
    case 'handover_undone': return n > 0 ? `Undid the handover of ${n}` : 'Undid a handover';
    case 'handover_was':    return n > 0 ? `Handed over ${n} — undone` : 'Handed over — undone';
    case 'dropped_was':     return n > 0 ? `Left ${n} to be collected — undone` : 'Left to be collected — undone';
    // #694, 79.22b.4.6.2 -- a count taken back, kept from its record and marked
    // undone, "of" what the truck carried (R1's drawing), and its Undo.
    case 'count_was':       return n > 0
      ? (sent > 0 ? `Counted in ${n} of ${sent} — undone` : `Counted in ${n} — undone`)
      : 'Counted in — undone';
    case 'count_undone':    return n > 0 ? `Undid the count of ${n}` : 'Undid a count';
    default:                return '';
  }
}

// ── #644's several-drops case, Session 79.22b.5.2 -- EVERY TRUCK WHOSE GOODS
// WERE IN THE PILE A COLLECTION TOOK FROM ─────────────────────────────────────
//
// Two Houston trucks leave goods at Austin; Dallas's driver takes them in one
// trip. The history named one of them ("from HOU-TRF-00014-4") and never the
// other. Colby ruled the narrowing not accepted (G4 at 79.22b.4.11) and chose
// this rule at 79.22b.5.2's open (R3): the line names every truck whose goods
// were in the pile when the press took from it.
//
// ITEM BY ITEM, the pile at the place the onward truck left from is replayed
// in time order -- what each truck left there, less what each earlier pickup
// took -- up to the moment the onward truck was loaded. Every truck that
// arrived since the pile last stood empty is in it. That is a reading of
// numbers already stored, the arithmetic hand_over_load() uses for "standing
// here", so no database change. A truck the collection itself ended arrives at
// the same instant it loads, and an arrival is replayed before a departure at
// one instant, as the collection writes them. A truck taken back (undone) never
// moved anything and is left out. Nothing found -- a collection older than
// this data, say -- returns []; the caller falls back to `prev_load_id`.
export function pileSources(loads, onward) {
  const all = Array.isArray(loads) ? loads : [];
  if (!onward || onward.prev_load_id == null || !onward.loaded_at) return [];
  const place = onward.from_place ? String(onward.from_place).trim() : null;
  const branchId = place ? null : (onward.from_branch_id ?? null);
  if (branchId == null && !place) return [];
  const endsHere = l => (place
    ? !!l.to_place && String(l.to_place).trim() === place
    : !l.to_place && String(l.to_branch_id) === String(branchId));
  const leavesHere = l => (place
    ? !!l.from_place && String(l.from_place).trim() === place
    : !l.from_place && String(l.from_branch_id) === String(branchId));
  const linesOf = l => l.branch_transfer_load_items || l.items || [];
  const at = v => new Date(v).getTime();
  const t0 = at(onward.loaded_at);
  const found = new Map();
  for (const itemId of new Set(linesOf(onward).map(li => String(li.item_id)))) {
    const events = [];
    for (const l of all) {
      if (!l || l.status === 'undone' || String(l.id) === String(onward.id)) continue;
      if (onward.transfer_id != null && String(l.transfer_id) !== String(onward.transfer_id)) continue;
      const mine = linesOf(l).filter(li => String(li.item_id) === itemId);
      if (endsHere(l) && l.arrived_at) {
        const q = mine.reduce((n, li) => n + num(li.qty_arrived), 0);
        if (q > 0) events.push({ t: at(l.arrived_at), order: 0, q, load: l });
      }
      if (leavesHere(l) && l.loaded_at) {
        const q = mine.reduce((n, li) => n + num(li.qty_loaded), 0);
        if (q > 0) events.push({ t: at(l.loaded_at), order: 1, q: -q, load: l });
      }
    }
    events.sort((a, b) => a.t - b.t || a.order - b.order || num(a.load.id) - num(b.load.id));
    let pile = 0;
    let since = [];
    for (const e of events) {
      if (e.t > t0) break;
      pile += e.q;
      if (e.order === 0) since.push(e.load);
      if (pile <= 0) { pile = 0; since = []; }
    }
    for (const l of since) found.set(String(l.id), l);
  }
  return [...found.values()].sort((a, b) => at(a.arrived_at) - at(b.arrived_at) || num(a.id) - num(b.id));
}

// ── #682 / #702, Session 79.22b.6c.2 -- A CORRECTION IS ONE LINE (682 D10) ──
//
// "Signed off ~~100~~ 40 · corrected by Owen Pike · Wrong number · 9:42 am". A
// correction takes the whole record back and writes the right number fresh, so
// the stock history holds both entries -- and the transfer's history shows them
// as ONE line: the step's FIRST number struck, its LATEST beside it, with the
// latest corrector, reason and time. The take-back and the fresh record are not
// lines of their own. H14's reconciliation still adds up: the line carries the
// latest number, which is what the transfer's counts now say.
//
// A line also says whether it can be corrected and by whom (`correct`), so the
// history can put Correct on it for the people canCorrectStep() lets in (682
// D3, D6). Who may is a permission and is asked by the screen; WHETHER the step
// can be corrected at all is a fact about the records, asked here, and it is
// the database's own test each time:
//   * a sign-off: its latest live run, never one taken back by its Undo, and
//     never one corrected to 0 -- nothing is left to correct, and To make offers
//     the sign-off again (decision 3 at 79.22b.6c.2's open);
//   * a truck: a first truck leaving the branch the goods stood at, not taken
//     back, nothing of it gone on from a stop (682 D2, narrowed);
//   * a count: a truck with a LIVE COUNT RECORD at its destination. A count made
//     before those records existed (Sep 27) has none, and the database could
//     never find it to correct (#682's amendment at 79.22b.6c.2's open).
// On a finished transfer the button stays and answers (S2, #492); while the
// other branch is asking about a truck's extra, that line answers with the
// next step instead (682 D5, F2).
const FINISHED_SENTENCE = 'This transfer is finished. Put the shelf right with a stock count.';

function chainOf(corrections) {
  const list = [...corrections].sort((a, b) => Number(a.id) - Number(b.id));
  if (!list.length) return null;
  const first = list[0];
  const last = list[list.length - 1];
  return {
    was: Number(first.was_qty), now: Number(last.now_qty), last,
    by: last.corrected_by_name || null, byId: last.corrected_by || null,
    at: stamp(last.corrected_at),
    reason: reasonText(last.reason, last.note),
    confirmed: last.reason === 'counted_more',
  };
}

export function transferHistory(loads, { branchNames = {}, usersById = {}, transfer = null, items = {}, production = [], handovers = [], countIns = [], corrections = [], extras = [] } = {}) {
  const out = [];
  const list = Array.isArray(loads) ? loads : [];
  // #694, 79.22b.4.6.1b -- the collections, and which trucks one took back.
  const records = Array.isArray(handovers) ? handovers : [];
  const undoneOnward = new Set(records
    .filter(r => r && r.undone_at && r.onward_load_id != null)
    .map(r => String(r.onward_load_id)));
  // #682 / #702 -- the corrections, and the questions still open about a truck
  // extra (one per truck line at most, by the database's own rule).
  const corr = (Array.isArray(corrections) ? corrections : []).filter(Boolean);
  const openAsk = new Map((Array.isArray(extras) ? extras : [])
    .filter(x => x && x.status === 'asked')
    .map(x => [String(x.load_item_id), x]));
  // #682, 79.22b.6c.2b -- every question by its truck, for the count's own line
  // (waiting, confirmed) and the alerts after No or Settle (S5, F2).
  const asksByLoad = new Map();
  for (const x of (Array.isArray(extras) ? extras : [])) {
    if (!x || x.load_id == null) continue;
    const k = String(x.load_id);
    if (!asksByLoad.has(k)) asksByLoad.set(k, []);
    asksByLoad.get(k).push(x);
  }
  const transferOpen = !!transfer && ['approved', 'in_transit'].includes(transfer.status);
  const senderName = transfer ? (branchNames[String(transfer.from_branch_id)] || 'The sending branch') : 'The sending branch';
  const destName = transfer ? (branchNames[String(transfer.to_branch_id)] || 'The branch it is for') : 'The branch it is for';

  // #644, Session 79.22b.1 -- A COLLECTION NAMED THE TRUCK IT CREATED AND NEVER
  // THE PILE IT CLEARED.
  //
  // A truck drops 25 frames at Austin. Two days later somebody fetches them in
  // two runs, and the history read "Loaded 10 ... HOU-TRF-00014-2" and "Loaded
  // 15 ... HOU-TRF-00014-3" -- each naming the new leg and nothing about where
  // the material came off. Inside one transfer a reader works it out from the
  // place and the order, and that stops working the moment two drops sit at the
  // same hub, which is the case hubs exist for.
  //
  // The link was already stored and rendered nowhere: `prev_load_id`, populated
  // and correct on every collection, checked against the live database on two
  // transfers before this was written. So this is a rendered field, not a
  // schema change and not a backfill.
  //
  // ** IT NAMES ONE PARENT, AND THE ISSUE ASKED FOR ALL OF THEM. ** That is not
  // a shortcut -- hand_over_load() records `prev_load_id = p_load_ids[1]`, and
  // its own comment says why: "with two trucks merging into one, the chain has
  // to pick a parent, and the earliest named leg is the one the caller listed
  // first." So when one collection clears two drops, the second drop's link to
  // the new leg does not exist anywhere in the schema and cannot be rendered.
  // Naming the one that IS recorded beats naming none; inventing the set would
  // be worse than both. The other cleared legs still appear in the history on
  // their own "Left N to be collected" lines.
  //
  // ** SUPERSEDED at Session 79.22b.5.2 (#644, Colby's R3). ** The paragraph
  // above was true at 79.22b.1 and stopped being true at 79.22b.4.6.1b, when
  // #694 gave every collection a record listing each truck it ended. And the
  // pile those trucks left is readable from their own numbers: pileSources(),
  // above. So the line now names every truck whose goods were in the pile --
  // "HOU-TRF-00014-5 from HOU-TRF-00014-1 + HOU-TRF-00014-4", joined the way
  // H12 already joins the trucks one press ended -- plus any truck the
  // collection's record says it ended, which keeps a collection taken back
  // naming what it took. Still one line; `prev_load_id` is the fallback.
  const refById = new Map(list.filter(l => l && l.id != null).map(l => [String(l.id), l.load_ref]));
  const loadsById = new Map(list.filter(l => l && l.id != null).map(l => [String(l.id), l]));
  // THE PARENT'S REFERENCE, or nothing at all. The first leg of any journey has
  // no predecessor, and a row reading "from none" would be worse than the
  // silence it replaced.
  const cameFrom = (l) => {
    if (!l || l.prev_load_id == null) return null;
    const named = new Map(pileSources(list, l).map(s => [String(s.id), s]));
    const record = records.find(r => r && String(r.onward_load_id) === String(l.id));
    for (const leg of (record && Array.isArray(record.closed_legs) ? record.closed_legs : [])) {
      const legLoad = loadsById.get(String(leg && leg.load_id));
      if (legLoad && !named.has(String(legLoad.id))) named.set(String(legLoad.id), legLoad);
    }
    const refs = [...named.values()]
      .sort((a, b) => (new Date(a.arrived_at || 0) - new Date(b.arrived_at || 0)) || (Number(a.id) - Number(b.id)))
      .map(s => s.load_ref).filter(Boolean);
    if (refs.length) return refs.join(' + ');
    const prev = refById.get(String(l.prev_load_id));
    return prev || null;
  };

  // ── H3 — THE TRANSFER'S OWN EVENTS, JOINED TO THE LOAD EVENTS ────────────
  //
  // This function took `transfer` and never read it, which is the entire reason
  // the history could not say who approved a transfer. The loads describe the
  // JOURNEY; everything that happened before a truck was involved lived only in
  // columns nobody rendered.
  //
  // THE ASKING BRANCH'S APPROVAL WAS NOT EVEN STORED until Session 79.20b.
  // `approved_by` belongs to the SUPPLYING branch's confirmation, and
  // endorse_branch_transfer() recorded nobody at all - so "who let this leave
  // our branch" had no answer anywhere in the schema. `endorsed_by` is that
  // answer, and this is the screen it was added for.
  if (transfer) {
    const asking = branchNames[String(transfer.to_branch_id)] || 'the asking branch';
    const supplying = branchNames[String(transfer.from_branch_id)] || 'the supplying branch';

    if (transfer.requested_at) {
      out.push({
        key: 't:requested', kind: 't:requested', qty: null, at: stamp(transfer.requested_at), ref: null,
        what: 'Requested', who: personName(transfer.requested_by, usersById),
        driver: null, handoverTo: null, where: asking, wherePlace: false,
        tone: 'neutral',
      });
    }
    if (transfer.endorsed_at) {
      // #576, RULED BY COLBY at Session 79.21: KEEP THE LINE, SAY THE TIME WAS
      // NOT RECORDED. Every transfer approved before Session 79.20b had its
      // `endorsed_at` BACKFILLED from `approved_at` so the stage guard could read
      // it - and printed, that is a time nobody recorded, shown to the second
      // beside the supplier's confirmation. `endorsed_by` is the tell: the real
      // endorse function always stamps it, and the backfill never did.
      //
      // Removing the line was the other choice, and gate 515_D37's H6 is why it
      // lost: "a history that quietly omits the actor is worse than one that
      // admits it." The approval happened; only its record is missing.
      //
      // The backfilled value still decides where the line SORTS - it equals the
      // confirmation instant, so the line keeps its place between Requested and
      // Confirmed - but it is never shown.
      const recorded = transfer.endorsed_by != null;
      // #698 half (b), Session 79.22b.4.7.1 -- WHAT WAS NOT NEEDED, AND BY
      // WHOM, on the approval that decided it (gate 665 X1: the history says
      // the asking branch refused it, never the supplier). The words Colby
      // approved at the open: "Asked San Antonio for 2 of 3 · not needed:
      // Stake Push 24". Nothing when every item was needed.
      const asked = Array.isArray(transfer.branch_transfer_items) ? transfer.branch_transfer_items : [];
      const notNeeded = asked.filter(l => refusedByAsker(l, transfer));
      const notNeededDetail = notNeeded.length > 0 && asked.length > notNeeded.length
        ? `Asked ${supplying} for ${asked.length - notNeeded.length} of ${asked.length} · not needed: `
          + notNeeded.map(l => items[String(l.item_id)]?.name || `item ${l.item_id}`).join(', ')
        : null;
      out.push({
        key: 't:endorsed', kind: 't:endorsed', qty: null,
        at: recorded ? stamp(transfer.endorsed_at) : null,
        sortAt: stamp(transfer.endorsed_at),
        timeNotRecorded: !recorded,
        ref: null,
        what: `Approved by ${asking}`, who: personName(transfer.endorsed_by, usersById),
        detail: notNeededDetail,
        driver: null, handoverTo: null, where: asking, wherePlace: false,
        tone: 'neutral',
      });
    }
    if (transfer.approved_at) {
      // #573, approved by Colby at Session 79.21 - A PARTLY REFUSED TRANSFER SAYS
      // SO ON ITS CONFIRMATION LINE. When the supplier answers item by item,
      // every item is answered in one action (answer_branch_transfer_lines()
      // refuses a partial set), so the refusal's person and moment ARE this
      // line's. A second line would print the same name at the same minute.
      //
      // Only when the answer was genuinely mixed. All accepted needs no sub-line;
      // all refused never reaches `approved` and gets its own Refused line below.
      //
      // #698 half (b), Session 79.22b.4.7 -- ONLY THE SUPPLIER'S OWN REFUSALS.
      // An item the asking branch turned down at the first stage was never put
      // to the supplier, so it is neither "can't send" nor counted in "of"
      // (gate 665 X2).
      const lines = Array.isArray(transfer.branch_transfer_items) ? transfer.branch_transfer_items : [];
      const refused = lines.filter(l => refusedBySupplier(l, transfer));
      const accepted = lines.filter(l => l.line_status === 'accepted');
      const detail = refused.length > 0 && accepted.length > 0
        ? `Sending ${accepted.length} of ${accepted.length + refused.length} · can't send `
          + refused.map(l => items[String(l.item_id)]?.name || `item ${l.item_id}`).join(', ')
        : null;
      out.push({
        key: 't:confirmed', kind: 't:confirmed', qty: null, at: stamp(transfer.approved_at), ref: null,
        what: `Confirmed by ${supplying}`, who: personName(transfer.approved_by, usersById),
        detail,
        driver: null, handoverTo: null, where: supplying, wherePlace: false,
        tone: 'neutral',
      });
    }
    // #573, approved by Colby at Session 79.21 - A WHOLE REFUSAL IS ITS OWN LINE.
    //
    // It was deliberately left out until now, and the reason still holds: there
    // was no `denied_at`, and dating a refusal from `updated_at` would print a
    // time that moves on any edit. `denied_by` and `denied_at` now exist and are
    // stamped by both refusal paths, so the line is listed ONLY when they were
    // recorded. A refusal from before that migration stays off the list rather
    // than acquiring an invented time - the detail's header still states it.
    //
    // WHICH BRANCH is refusingBranchId(), the same rule the detail header uses.
    if (transfer.denied_at) {
      const refuser = branchNames[String(refusingBranchId(transfer))]
        || (transfer.endorsed_at ? supplying : asking);
      out.push({
        key: 't:denied', kind: 't:denied', qty: null, at: stamp(transfer.denied_at), ref: null,
        what: `Refused by ${refuser}`, who: personName(transfer.denied_by, usersById),
        driver: null, handoverTo: null, where: refuser, wherePlace: false,
        tone: 'refused',
      });
    }
    // #590, gate 371 F2 -- A WITHDRAWAL IS ITS OWN LINE, naming the asking
    // branch, and the word "refused" appears nowhere: the supplying branch
    // refused nothing (callout G). Listed only when it was recorded.
    if (transfer.cancelled_at) {
      out.push({
        key: 't:withdrawn', kind: 't:withdrawn', qty: null, at: stamp(transfer.cancelled_at), ref: null,
        what: transfer.cancel_reason ? `Withdrawn — \u201c${transfer.cancel_reason}\u201d` : 'Withdrawn',
        who: personName(transfer.cancelled_by, usersById),
        driver: null, handoverTo: null, where: asking, wherePlace: false,
        tone: 'waiting',
      });
    }
    // THE CLOSE. `closed_by_branch_id` is read rather than assumed, because
    // either branch can close a transfer short.
    if (transfer.closed_at) {
      const closer = branchNames[String(transfer.closed_by_branch_id)] || null;
      // 371 D33 -- the ASKING branch stopping the rest reads as its decision,
      // in its words: "{asking branch} doesn't need the rest — reason".
      const askerStopped = transfer.status === 'closed_unfilled'
        && transfer.closed_by_branch_id != null
        && String(transfer.closed_by_branch_id) === String(transfer.to_branch_id);
      // #719, Session 79.22b.6d, the approved drawing -- A WHOLE REFUSAL CLOSED
      // WITH "NOTHING MORE IS NEEDED" IS THE ASKING BRANCH'S DECISION, NOT A
      // STOP. route_denied_transfer(..., 'none') closes the same way W7 does and
      // writes its own stock sentence to unfilled_reason, so the W7 line quoted
      // words nobody typed and said "the rest" when nothing was sent. Told
      // apart by the database's own test: it closes only a transfer with NO
      // accepted item, and W7 always has something agreed. The items must have
      // been read -- with none in hand this stays the W7 line.
      const closedLines = Array.isArray(transfer.branch_transfer_items) ? transfer.branch_transfer_items : [];
      const decidedNothingNeeded = askerStopped && closedLines.length > 0
        && !closedLines.some(l => l.line_status === 'accepted');
      out.push({
        key: 't:closed', kind: 't:closed', qty: null, at: stamp(transfer.closed_at), ref: null,
        what: decidedNothingNeeded
          ? `${asking} decided nothing more is needed`
          : askerStopped
          ? `${asking} doesn't need the rest${transfer.unfilled_reason ? ` — \u201c${transfer.unfilled_reason}\u201d` : ''}`
          : transfer.status === 'closed_unfilled' ? 'Closed — nothing more coming'
          : transfer.status === 'closed_short' ? 'Closed short'
            : 'Closed',
        who: personName(transfer.closed_by, usersById),
        driver: null, handoverTo: null, where: closer || asking, wherePlace: false,
        tone: 'waiting',
      });
    }

    // A REFUSAL IS LISTED ABOVE SINCE #573, and only where it was recorded - see
    // the note on `t:denied`. Until Session 79.21 it was deliberately NOT listed,
    // because there was no `denied_at` and `updated_at` moves on any edit.
    // Inventing a timestamp to make the list look complete is the thing this
    // file's history rules exist to stop, and that rule is unchanged.
    //
    // `shipped_at` and `received_at` are skipped: the loads already carry
    // both, per load, with quantities. Adding the header's copy would print
    // every departure and arrival twice.
  }

  // ── #603 -- EVERY RUN THAT WAS SIGNED OFF, in the journey where it happened.
  //
  // Gate 553: "the sign-off on Thursday appears in the journey even though
  // nothing moved, because a fabricator finishing 60 frames is part of the
  // story of why Dallas is still waiting. It is also the line that proves the
  // make step now has a person and a time."
  //
  // WHERE is the branch that MADE them -- a run is not a movement, and drawing
  // it at a destination would read like an arrival. The name falls back to the
  // one the database stored at sign-off, so a person who has since left still
  // has their name on the run they signed.
  //
  // #694, gate 665 B6, Session 79.22b.4.6 -- AN UNDO IS ITS OWN LINE, never an
  // erasure. Its run is negative and names the run it took back (reverses_id),
  // so it reads "Undid the sign-off of 30" with who, when and where, and the run
  // it names says "\u2014 undone": a reader sees the press AND what happened to it.
  const runs = Array.isArray(production) ? production : [];
  // #682 / #702 -- a correction's take-back run and its fresh run carry the
  // correction's id; both are drawn on the line of the step they corrected, so
  // the take-back is no Undo and the original is not "— undone".
  const undoneRuns = new Set(runs.filter(r => r?.reverses_id != null && r?.correction_id == null).map(r => String(r.reverses_id)));
  const freshRunOf = new Map(runs.filter(r => r?.reverses_id == null && r?.correction_id != null)
    .map(r => [String(r.correction_id), r]));
  // The runs alone say a step was corrected: a take-back carrying a correction's
  // id. So the line is right even before the correction records are read --
  // who and why fill in when they are.
  const takenBackBy = new Map(runs.filter(r => r?.reverses_id != null && r?.correction_id != null)
    .map(r => [String(r.reverses_id), r]));
  const signOffFix = new Map(corr.filter(c => c.kind === 'sign_off' && c.original_production_id != null)
    .map(c => [String(c.original_production_id), c]));
  const signOffOpen = transferOpen || (!!transfer && transfer.status === 'received' && isOwnShelf(transfer));
  for (const r of runs) {
    if (!r?.signed_off_at) continue;
    if (r.correction_id != null) continue;
    const undo = r.reverses_id != null;
    const qty = Math.abs(Number(r.qty) || 0);
    const name = r.item_id != null
      ? (items[String(r.item_id)]?.name || null)
      : null;
    // The item is named only when there is more than one on the transfer:
    // on a single-item request it is the only thing it could be.
    const manyItems = ((transfer?.branch_transfer_items || []).length) > 1;
    // An Undo's own note is the database's "Undone", which its label already says.
    const note = !undo && typeof r.notes === 'string' && r.notes.trim() ? r.notes.trim() : null;
    const madeAt = transfer?.from_branch_id ?? null;
    // THE STEP'S CHAIN: this run, each correction that took the step's number
    // back, and the fresh run each one wrote -- ending at the latest live run,
    // or at none when the step was corrected to 0.
    const chain = [];
    let live = undo ? null : r;
    for (let guard = 0; live && guard < 100; guard++) {
      const back = takenBackBy.get(String(live.id));
      if (!back) break;
      const fresh = freshRunOf.get(String(back.correction_id)) || null;
      chain.push(signOffFix.get(String(live.id)) || {
        id: back.correction_id, was_qty: Math.abs(Number(live.qty) || 0),
        now_qty: fresh ? Math.abs(Number(fresh.qty) || 0) : 0,
        corrected_by: back.signed_off_by ?? null, corrected_by_name: back.signed_off_by_name ?? null,
        corrected_at: back.signed_off_at, reason: null, note: null,
      });
      live = fresh;
    }
    const fixed = chain.length ? chainOf(chain) : null;
    if (fixed) fixed.was = qty;
    const correct = !undo && live && !undoneRuns.has(String(live.id)) && madeAt != null ? {
      kind: 'sign_off', branchId: madeAt, blocked: signOffOpen ? null : FINISHED_SENTENCE,
      items: [{
        id: live.id, lineId: live.transfer_item_id ?? r.transfer_item_id, itemId: r.item_id ?? null, name,
        qty: Math.abs(Number(live.qty) || 0),
        recordedBy: live.signed_off_by ?? null,
        recordedByName: personName(live.signed_off_by, usersById) || live.signed_off_by_name || null,
        at: stamp(live.signed_off_at), blocked: null,
      }],
    } : null;
    if (fixed) {
      out.push({
        key: `p:${r.id}`, kind: 'signed_off', qty: fixed.now, ref: null,
        at: fixed.at, sortAt: stamp(r.signed_off_at),
        what: historyLabel('signed_off', fixed.now) + (manyItems && name ? ` ${name}` : ''),
        corrected: { label: 'Signed off', was: fixed.was, now: fixed.now, item: manyItems && name ? name : null,
          by: fixed.by || personName(fixed.byId, usersById), reason: fixed.reason, confirmed: null },
        who: fixed.by || personName(fixed.byId, usersById),
        driver: null, handoverTo: null,
        where: branchNames[String(transfer?.from_branch_id)] || null, wherePlace: false,
        tone: 'neutral',
        correct: fixed.now > 0 ? correct : null,
      });
      continue;
    }
    out.push({
      key: `p:${r.id}`, kind: undo ? 'sign_off_undone' : 'signed_off', qty, ref: null,
      at: stamp(r.signed_off_at),
      what: historyLabel(undo ? 'sign_off_undone' : 'signed_off', qty)
        + (manyItems && name ? ` ${name}` : '')
        + (note ? ` \u2014 \u201c${note}\u201d` : '')
        + (!undo && undoneRuns.has(String(r.id)) ? ' \u2014 undone' : ''),
      who: personName(r.signed_off_by, usersById) || r.signed_off_by_name || null,
      driver: null, handoverTo: null,
      where: branchNames[String(transfer?.from_branch_id)] || null, wherePlace: false,
      tone: 'neutral',
      correct,
    });
  }

  for (const l of list) {
    const ends = legEnds(l, branchNames);
    const rows = l.branch_transfer_load_items || l.items || [];
    const sent = rows.reduce((n, r) => n + (Number(r.qty_loaded) || 0), 0);
    const got  = rows.reduce((n, r) => n + (r.qty_arrived == null ? 0 : Number(r.qty_arrived)), 0);

    const presser = personName(l.loaded_by, usersById);
    // A driver is only NAMED as a driver when they are distinguishable from
    // whoever pressed the button. See the note above.
    const namedDriver = l.driver_name
      || (l.driver_id && String(l.driver_id) !== String(l.loaded_by)
            ? personName(l.driver_id, usersById)
            : null);

    // #682 / #702 -- THIS TRUCK'S CORRECTIONS, and its count's, item by item. A
    // truck's line keeps one row per item, so its number changes on that row;
    // the chain says what it was first and who changed it last (682 D1, D10).
    const itemName = li => items[String(li.item_id)]?.name || li.inventory_items?.name || `item ${li.item_id}`;
    const fixesOf = (kind, extra = () => true) => {
      const m = new Map();
      for (const li of rows) {
        const cs = corr.filter(c => c.kind === kind && String(c.load_item_id) === String(li.id) && extra(c));
        if (cs.length) m.set(String(li.id), chainOf(cs));
      }
      return m;
    };
    const truckFixes = fixesOf('load');
    // The count's LIVE record: what correct_count_in() corrects, and only it.
    const countRecord = (Array.isArray(countIns) ? countIns : [])
      .find(c => c && String(c.load_id) === String(l.id) && !c.undone_at) || null;
    const countFixes = countRecord
      ? fixesOf('count', c => String(c.count_in_id) === String(countRecord.id))
      : new Map();
    const atName = branchNames[String(l.to_branch_id)] || destName;
    const fromName = branchNames[String(l.from_branch_id)] || senderName;
    // #682, 79.22b.6c.2b -- THE COUNT'S OWN LINE NAMES THE TRUCK'S EXTRA (F2):
    // while the sender has not answered, "Counted in 2 · 2 more waiting on
    // Houston"; once it confirmed, "Counted in 4 · 2 confirmed by Houston" (S6).
    // A question its count's Undo withdrew says nothing.
    const asks = (asksByLoad.get(String(l.id)) || []).filter(x => x.status !== 'withdrawn');
    const waitingExtra = asks.filter(x => x.status === 'asked').reduce((n, x) => n + num(x.extra_qty), 0);
    const confirmedExtra = asks.filter(x => x.status === 'confirmed').reduce((n, x) => n + num(x.extra_qty), 0);
    const countedWhat = (n, base = historyLabel('counted', n)) => {
      if (waitingExtra > 0) return `${base} · ${qtyWords(waitingExtra)} more waiting on ${fromName}`;
      if (confirmedExtra > 0) return `Counted in ${qtyWords(n)} · ${qtyWords(confirmedExtra)} confirmed by ${fromName}`;
      return base;
    };
    // One corrected item: the drawn single line. Several: the truck's total,
    // and a line per corrected item beneath it (F2).
    const decorate = (entry, label, fixes) => {
      if (!fixes.size) return entry;
      const confirmedBy = ch => (ch.confirmed ? { counted: ch.now, place: atName } : null);
      if (rows.length === 1) {
        const ch = fixes.get(String(rows[0].id));
        if (!ch) return entry;
        const by = ch.by || personName(ch.byId, usersById);
        return {
          ...entry, at: ch.at, sortAt: entry.at, who: by,
          corrected: { label, was: ch.was, now: ch.now, item: null, by, reason: ch.reason, confirmed: confirmedBy(ch) },
        };
      }
      return {
        ...entry,
        subLines: rows.filter(li => fixes.has(String(li.id))).map(li => {
          const ch = fixes.get(String(li.id));
          return {
            key: String(li.id), item: itemName(li), was: ch.was, now: ch.now,
            by: ch.by || personName(ch.byId, usersById), reason: ch.reason, confirmed: confirmedBy(ch),
          };
        }),
      };
    };
    // A FIRST TRUCK LEAVING THE BRANCH THE GOODS STOOD AT, not a hub's goods
    // collected by the branch they are for, nothing of it gone on from a stop --
    // the only trucks correct_load() takes (682 D2, narrowed). #682's amendment
    // at 79.22b.6c.2b's open: this tested the first two clauses only, so a hub
    // collection's truck offered Correct the database always refuses.
    const firstFromOrigin = senderCanPutRight(l, transfer);
    const goneOn = list.some(x => x && String(x.prev_load_id) === String(l.id))
      || records.some(h => h && !h.undone_at && h.onward_load_id != null
        && (Array.isArray(h.closed_legs) ? h.closed_legs : []).some(leg => String(leg?.load_id) === String(l.id)));
    const truckCorrect = firstFromOrigin && l.status !== 'undone' && !goneOn ? {
      kind: 'load', branchId: l.from_branch_id, loadId: l.id, loadRef: l.load_ref, dest: destName,
      blocked: transferOpen ? null : FINISHED_SENTENCE,
      items: rows.map(li => {
        const ch = truckFixes.get(String(li.id));
        const ask = openAsk.get(String(li.id));
        return {
          id: li.id, itemId: li.item_id, name: itemName(li), qty: Number(li.qty_loaded) || 0,
          counted: li.qty_arrived == null ? null : Number(li.qty_arrived),
          recordedBy: ch ? ch.byId : (l.loaded_by ?? null),
          recordedByName: ch ? (ch.by || personName(ch.byId, usersById)) : presser,
          at: ch ? ch.at : stamp(l.loaded_at),
          blocked: ask
            ? `${atName} is asking about this truck's extra ${qtyText(ask.extra_qty)} — answer it on Awaiting confirmation first.`
            : null,
        };
      }),
    } : null;
    // A COUNT WITH ITS LIVE RECORD, made at the transfer's destination -- the
    // only counts correct_count_in() takes. A count from before Sep 27 has no
    // record and gets no button (#682's amendment at 79.22b.6c.2's open).
    const countCorrect = !!transfer && countRecord && countRecord.at_destination
      && String(countRecord.at_branch_id) === String(transfer.to_branch_id) ? {
      kind: 'count', branchId: countRecord.at_branch_id, loadId: l.id, loadRef: l.load_ref,
      countInId: countRecord.id, sender: fromName, askable: firstFromOrigin,
      blocked: transferOpen ? null : FINISHED_SENTENCE,
      items: rows.map(li => {
        const ch = countFixes.get(String(li.id));
        const ask = openAsk.get(String(li.id));
        return {
          id: li.id, itemId: li.item_id, name: itemName(li),
          qty: li.qty_arrived == null ? 0 : Number(li.qty_arrived),
          loaded: Number(li.qty_loaded) || 0, unitCost: li.unit_cost_at_transfer ?? null,
          recordedBy: ch ? ch.byId : (countRecord.created_by ?? null),
          recordedByName: ch ? (ch.by || personName(ch.byId, usersById)) : personName(countRecord.created_by, usersById),
          at: ch ? ch.at : stamp(l.counted_at || countRecord.created_at),
          blocked: ask
            ? `${fromName} hasn't answered about the extra ${qtyText(ask.extra_qty)} yet — correct this count after it does.`
            : null,
        };
      }),
    } : null;

    if (l.loaded_at) {
      // #644 -- APPENDED TO THE REFERENCE, NEVER A NEW EVENT. The history is
      // condensed to the last three events with the rest behind "View full
      // history - n earlier", so pushing a row here would move that count and
      // the window with it. This is one row carrying one more fact.
      const from = cameFrom(l);
      // #694, 79.22b.4.6.1 -- A TRUCK TAKEN BACK KEEPS ITS LINE and says so; the
      // Undo is its own line below, never an erasure (gate 665 B6).
      const undone = l.status === 'undone';
      out.push(decorate({
        key: `${l.id}:loaded`, at: stamp(l.loaded_at),
        ref: from ? `${l.load_ref} from ${from}` : l.load_ref,
        kind: 'loaded', qty: sent,
        what: historyLabel('loaded', sent) + (undone ? ' — undone' : ''),
        who: presser, driver: namedDriver, handoverTo: null,
        where: ends.from, wherePlace: ends.fromIsPlace,
        tone: 'neutral',
        correct: truckCorrect,
      }, 'Loaded', truckFixes));
    }

    // #694 -- THE UNDO, with who, when and where: the person who pressed Send,
    // at the branch the truck was to leave from. Its quantity is the load's, so
    // what left is what was loaded less what was undone (H14's reconciliation).
    // 79.22b.4.6.1b -- a truck a COLLECTION opened, taken back with it, reads
    // "Undid the collection of 5": the record says whose Undo it was.
    if (l.status === 'undone' && l.undone_at) {
      const undoKind = undoneOnward.has(String(l.id)) ? 'collect_undone' : 'send_undone';
      out.push({
        key: `${l.id}:undone`, at: stamp(l.undone_at), ref: l.load_ref,
        kind: undoKind, qty: sent, what: historyLabel(undoKind, sent),
        who: personName(l.undone_by, usersById), driver: null, handoverTo: null,
        where: ends.from, wherePlace: ends.fromIsPlace,
        tone: 'neutral',
      });
    }

    // THE HANDOVER IS THE ONE EVENT WITH TWO PEOPLE IN IT, and the arrow is
    // what makes a change of custody readable at a glance rather than
    // reconstructed from two rows.
    if (l.ended_as === 'handed_over' && l.arrived_at) {
      out.push({
        key: `${l.id}:handover`, at: stamp(l.arrived_at), ref: l.load_ref,
        kind: 'handover', qty: got, what: historyLabel('handover', got),
        // #728 -- the giver by the bell's rule, never the loader by default.
        who: handoverGiver(l, { nameOf: id => personName(id, usersById), branchNames }), driver: null,
        handoverTo: receivedBy(l, usersById),
        where: ends.to, wherePlace: ends.toIsPlace,
        tone: 'neutral',
      });
    } else if (l.ended_as === 'dropped' && l.arrived_at) {
      out.push({
        key: `${l.id}:dropped`, at: stamp(l.arrived_at), ref: l.load_ref,
        kind: 'dropped', qty: got, what: historyLabel('dropped', got),
        who: personName(l.arrived_by, usersById) || namedDriver || presser, driver: null, handoverTo: null,
        where: ends.to, wherePlace: ends.toIsPlace,
        // H5, ruled by Colby: the hub drop reads BLUE and matches the To collect
        // tab, so the one line in the history that means "somebody still has to
        // go and get this" is the same colour as the tab that lists it.
        //
        // It is its OWN tone rather than reusing 'waiting'. Both mean unfinished
        // and they are different jobs: 'waiting' is a count nobody has done yet
        // at a branch that already has the material; 'collect' is material on
        // the ground at a hub with nobody employed there. That is #529's whole
        // finding, and giving them one colour puts it back.
        tone: 'collect',
      });
    } else if (l.arrived_at) {
      // ARRIVING AND COUNTING ARE TWO EVENTS SINCE D17 -- but on every leg
      // recorded before it, pressing arrive WAS the count and both columns hold
      // the same instant. Printing two lines with one timestamp would invent a
      // history that did not happen, so they collapse when the times match.
      const sameMoment = l.counted_at && l.arrived_at
        && new Date(l.counted_at).getTime() === new Date(l.arrived_at).getTime();
      const arrivedLine = {
        key: `${l.id}:arrived`, at: stamp(l.arrived_at), ref: l.load_ref,
        kind: sameMoment ? 'arrived_counted' : 'arrived',
        qty: sameMoment ? got : null,
        what: sameMoment ? countedWhat(got, historyLabel('arrived_counted', got)) : historyLabel('arrived', got),
        who: personName(l.arrived_by, usersById) || namedDriver || presser,
        driver: null, handoverTo: null,
        where: ends.to, wherePlace: ends.toIsPlace,
        tone: sameMoment ? 'landed' : 'waiting',
      };
      // #682 / #702 -- the count's Correct, and its corrected line, sit on the
      // line that shows the count: this one when arriving WAS the count.
      out.push(sameMoment
        ? decorate({ ...arrivedLine, correct: countCorrect }, 'Counted in', countFixes)
        : arrivedLine);
      if (!sameMoment && l.counted_at) {
        out.push(decorate({
          key: `${l.id}:counted`, at: stamp(l.counted_at), ref: l.load_ref,
          kind: 'counted', qty: got, what: countedWhat(got),
          who: personName(l.counted_by, usersById), driver: null, handoverTo: null,
          where: ends.to, wherePlace: ends.toIsPlace,
          tone: 'landed',
          correct: countCorrect,
        }, 'Counted in', countFixes));
      }
    }

    // #682, 79.22b.6c.2b -- S5 AND CALLOUT T: THE SENDER'S NO, OR SETTLE, REACHES
    // THE COUNTING BRANCH as a plain fact and a next step, an Alert in the
    // history at the moment of the answer, under "Count on {truck}" with whoever
    // counted it. Yes needs no row of its own: the count's line says "confirmed
    // by" (above), and the truck's own line says so too.
    // #682, 79.22b.6c.3 -- the words live in corrections.js beside S6's, because
    // the answer's bell says them too.
    for (const x of asks) {
      if (x.status !== 'declined' && x.status !== 'settled_by_count') continue;
      out.push({
        key: `${x.id}:answered`, at: stamp(x.answered_at), ref: `Count on ${l.load_ref}`,
        kind: 'extra_answered', alert: true,
        what: x.status === 'declined'
          ? extraNoSentence({ from: fromName, loaded: x.loaded_qty, extra: x.extra_qty })
          : extraSettleSentence({ from: fromName, extra: x.extra_qty, transferNumber: transfer?.transfer_number }),
        who: x.counted_by_name || personName(x.counted_by, usersById),
        driver: null, handoverTo: null, where: atName, wherePlace: false, tone: 'neutral',
      });
    }
  }

  // ── #694, gate 665 B6, Session 79.22b.4.6.1b -- A COLLECTION TAKEN BACK
  // KEEPS ITS LINES. ────────────────────────────────────────────────────────
  //
  // An Undo puts each truck the collection closed back as it was, so the
  // truck's own columns no longer say it was handed over -- and a history drawn
  // only from them would erase the handover. The collection's record kept what
  // it wrote, so the line is drawn from there, saying "— undone": a reader sees
  // the press AND what happened to it (B6). If the same truck is collected
  // again, its columns draw the new handover and this line keeps the old one.
  // Take nothing opened no truck, so its Undo is its own line here; a
  // collection that did open one says so on that truck's line, above.
  // FIRST ON A TIE: a handover and the truck it loaded share one moment (one
  // transaction), and the journey reads the handover first, as a live one does.
  const loadById = new Map(list.filter(l => l && l.id != null).map(l => [String(l.id), l]));
  const recordLines = [];
  for (const r of records) {
    if (!r || !r.undone_at) continue;
    const place = r.at_place || branchNames[String(r.at_branch_id)] || null;
    const legs = Array.isArray(r.closed_legs) ? r.closed_legs : [];
    let closedQty = 0;
    for (const leg of legs) {
      const a = (leg && leg.after) || {};
      const legLoad = loadById.get(String(leg && leg.load_id)) || null;
      const got = (Array.isArray(a.lines) ? a.lines : [])
        .reduce((n, x) => n + (x && x.qty_arrived != null ? Number(x.qty_arrived) : 0), 0);
      closedQty += got;
      const presser = legLoad ? personName(legLoad.loaded_by, usersById) : null;
      const namedDriver = legLoad
        ? (legLoad.driver_name
          || (legLoad.driver_id && String(legLoad.driver_id) !== String(legLoad.loaded_by)
            ? personName(legLoad.driver_id, usersById) : null))
        : null;
      const handedOver = a.ended_as === 'handed_over';
      const kind = handedOver ? 'handover_was' : 'dropped_was';
      recordLines.push({
        key: `h:${r.id}:${leg && leg.load_id}`, at: stamp(a.arrived_at), ref: legLoad ? legLoad.load_ref : null,
        kind, qty: got, what: historyLabel(kind, got),
        // #728 -- the same giver rule as the live row and the bell.
        who: handedOver
          ? (legLoad ? handoverGiver(legLoad, { nameOf: id => personName(id, usersById), branchNames }) : null)
          : (personName(a.arrived_by, usersById) || namedDriver || presser),
        driver: null,
        handoverTo: handedOver ? receivedBy(a, usersById) : null,
        where: place, wherePlace: r.at_branch_id == null,
        tone: 'neutral',
      });
    }
    if (r.onward_load_id == null) {
      const first = loadById.get(String(legs[0] && legs[0].load_id)) || null;
      recordLines.push({
        key: `h:${r.id}:undone`, at: stamp(r.undone_at), ref: first ? first.load_ref : null,
        kind: 'handover_undone', qty: closedQty, what: historyLabel('handover_undone', closedQty),
        who: personName(r.undone_by, usersById), driver: null, handoverTo: null,
        where: place, wherePlace: r.at_branch_id == null,
        tone: 'neutral',
      });
    }
  }
  out.unshift(...recordLines);

  // ── #694, gate 665 B6, Session 79.22b.4.6.2 -- A COUNT TAKEN BACK KEEPS ITS
  // LINE. ────────────────────────────────────────────────────────────────────
  //
  // An Undo puts the truck back as it was before the count, so its own columns
  // no longer say it was counted -- and a history drawn only from them would
  // erase the count. The count's record kept what the count wrote, so the line
  // is drawn from there, "Counted in 10 of 12 — undone" with who and when,
  // beside "Undid the count of 10" (B6: the press AND what happened to it). A
  // truck counted again after its Undo draws the new count from its own
  // columns, and this line keeps the old one.
  for (const r of (Array.isArray(countIns) ? countIns : [])) {
    if (!r || !r.undone_at) continue;
    const a = r.load_after || {};
    const counted = Array.isArray(a.lines) ? a.lines : [];
    const got = counted.reduce((n, x) => n + (x && x.qty_arrived != null ? Number(x.qty_arrived) : 0), 0);
    const carried = counted.reduce((n, x) => n + (Number(x && x.qty_loaded) || 0), 0);
    const truck = loadById.get(String(r.load_id)) || null;
    const where = branchNames[String(r.at_branch_id)] || null;
    out.push({
      key: `ci:${r.id}:was`, at: stamp(a.counted_at || r.created_at), ref: truck ? truck.load_ref : null,
      kind: 'count_was', qty: got, what: historyLabel('count_was', got, carried),
      who: personName(r.created_by, usersById), driver: null, handoverTo: null,
      where, wherePlace: false,
      tone: 'neutral',
    });
    out.push({
      key: `ci:${r.id}:undone`, at: stamp(r.undone_at), ref: truck ? truck.load_ref : null,
      kind: 'count_undone', qty: got, what: historyLabel('count_undone', got),
      who: personName(r.undone_by, usersById), driver: null, handoverTo: null,
      where, wherePlace: false,
      tone: 'neutral',
    });
  }

  // ── H12 — ONE HUB COLLECTION IS ONE LINE, NOT ONE PER LOAD ───────────────
  //
  // `hand_over_load()` loops over every inbound leg and stamps each with
  // `arrived_at = now()` and `arrived_by = auth.uid()`. Inside one transaction
  // now() is a single value, so two legs ended by one collection carry the SAME
  // instant and the SAME person - which is exactly the gate's objection to
  // rendering them separately: "it would invent events that did not happen and
  // put the same name and the same minute on each".
  //
  // THE GROUPING KEY IS THE EVENT ITSELF, not a guess: same kind, same exact
  // timestamp, same person, same place. Two genuinely separate arrivals cannot
  // collide on all four - they would have to be the same person in the same
  // place in the same millisecond doing the same thing, which IS one event.
  //
  // An undated event never merges: `at` is null and null is not a key anybody
  // shares. That keeps the transfer's own events out of this entirely.
  const merged = [];
  const groups = new Map();
  for (const e of out) {
    const t = e.at ? e.at.getTime() : null;
    // #682 / #702 -- a corrected line is one step's own, drawn from its own
    // chain, and never merges with another.
    if (t == null || !e.ref || e.corrected || e.subLines) { merged.push(e); continue; }
    const k = [e.kind, t, e.who || '', e.where || ''].join('|');
    if (!groups.has(k)) { groups.set(k, []); merged.push(e); continue; }
    groups.get(k).push(e);
    e.__absorbed = true;
  }
  if (groups.size) {
    for (const [k, extras] of groups) {
      if (!extras.length) continue;
      const head = merged.find(e => !e.__absorbed
        && [e.kind, e.at ? e.at.getTime() : null, e.who || '', e.where || ''].join('|') === k);
      if (!head) continue;
      const all = [head, ...extras];
      // THE QUANTITY IS THE SUM and the label is RE-RENDERED from it, which is
      // the whole reason `qty` exists separately from `what`.
      const total = all.reduce((n, e) => n + (Number(e.qty) || 0), 0);
      head.qty = total;
      head.what = historyLabel(head.kind, total);
      // NAMING THE LOADS IT ENDED is the gate's requirement, not decoration:
      // it is how somebody reconciling a shortfall knows which legs this line
      // accounts for.
      head.ref = all.map(e => e.ref).filter(Boolean).join(' + ');
      head.mergedFrom = all.length;
    }
  }
  const out2 = merged.filter(e => !e.__absorbed);
  out.length = 0;
  out.push(...out2);

  // OLDEST FIRST -- it is a journey, and a journey reads forwards. An undated
  // event sorts last rather than throwing the order away.
  //
  // `sortAt` is a position without a printable time: #576's older approval
  // keeps its place in the journey while its WHEN reads "not recorded". Ties
  // keep the order the events were pushed in, which is the order of the stages.
  const key = (e) => e.sortAt || e.at;
  return out.sort((a, b) => {
    const ka = key(a);
    const kb = key(b);
    if (!ka && !kb) return 0;
    if (!ka) return 1;
    if (!kb) return -1;
    return ka - kb;
  });
}

// ── WHO IS DRIVING — #553 D12 ────────────────────────────────────────────────
//
// A ONE-PERSON PICKER, never a setting, and never the free-text box that used
// to sit here. Colby's reasoning, and it decided the shape: whether a driver
// has a login is a fact about THAT DRIVER, not about the company. A tenant can
// run three employee drivers and a contract hauler at once, so a company-wide
// switch would be wrong for them in both positions. D&R's drivers all have
// logins, see themselves prefilled, and never open the escape.
//
// TWO COLUMNS, NEVER BOTH — the database says so. `driver_id` is a real
// account; `driver_name` is a typed string for somebody with no login; and
// `transfer_load_driver_one_way` refuses a row carrying both. Both null is
// legal and means nobody said.
//
// THE ESCAPE IS HONEST WHERE A TYPED STRING IS NOT. "Signed by Maria on behalf
// of Rick Doyle" records who actually pressed the button AND who drove. The
// old free-text box recorded a name attached to no account and called it the
// driver, which is B6's warning: a wrong name in an audit trail is worse than
// no name.
export const DRIVER_SOMEONE_ELSE = 'someone_else';

// WHO CAN APPEAR IN THE PICKER, read from the branch doing the loading.
//
// The roles mirror the gate's B4 line for "Hand over / take on" -- admin,
// manager and team_member yes, executive and purchasing no. FABRICATORS AND
// DRIVERS ARE team_members, and #369 is the precedent for why that matters:
// a team member could not adjust stock at all, so the people doing the work
// could not record it. Leave them out and the picker is empty at Dallas.
//
// `is_active` is not decoration either -- a picker offering somebody who has
// left the company puts their name in an audit trail.
//
// #699, gate 665 D28, Session 79.22b.5.1 -- AND WHO DRIVES. `drives_trucks` is
// the per-person switch "Drives trucks for transfers" (Settings -> Users). It is
// read here so the picker can offer drivers first, and by drivesHere() below.
export async function fetchBranchDrivers(branchId) {
  if (branchId == null) return [];
  const { data, error } = await supabase
    .from('users')
    .select('id, full_name, role, drives_trucks')
    .eq('branch_id', branchId)
    .eq('is_active', true)
    .in('role', ['admin', 'manager', 'team_member']);
  // A FAILED FETCH RETURNS AN EMPTY LIST RATHER THAN THROWING, and the screen
  // falls back to the escape field. An empty picker is indistinguishable from
  // a branch with no drivers, which is why the panel never renders a picker
  // with only the escape in it and calls it a choice.
  if (error) return [];
  return data || [];
}

// ── WHO DRIVES — #699, gate 665 D28, Session 79.22b.5.1 ─────────────────────
//
// A switch on the PERSON, never on the company: 553 D12's own reasoning, that
// driving is "a fact about that driver, not about the company". D12 ruled out
// a company switch, not this one.
//
// WITH NOBODY AT A BRANCH SWITCHED ON, NOTHING CHANGES FROM BEFORE: anyone at
// the branch counts as able to load and collect, which is the database's own
// rule (the physical acts check only that you are AT the branch). That is how a
// company opts in, and why one that never does loses nothing.
//
// ONCE ANYBODY IS SWITCHED ON, To load and To collect are "your job" only for
// the people switched on -- so a Dallas installer no longer opens Transfers on
// the Austin pickup. ADVISORY ONLY: it decides what a screen offers first and
// where it opens, never what anybody may do. `people` is the branch's list from
// fetchBranchDrivers(); `=== true` so a list fetched before the column existed
// reads as nobody switched on, never as everybody.
export function isDriver(person) {
  return person?.drives_trucks === true;
}

export function drivesHere(people, userId) {
  const list = Array.isArray(people) ? people : [];
  if (!list.some(isDriver)) return true;
  return userId != null && list.some(p => isDriver(p) && String(p.id) === String(userId));
}

// WHO IS PRE-FILLED AS THE DRIVER when the person pressing is asked nothing:
// them, if they are on the branch's list AND drive there. 79.22b.5.1's R1, call
// (a): a person who is not switched on, at a branch that has drivers, is not
// named -- a wrong name in the history is worse than none (553 B6). With nobody
// switched on this is exactly the old rule: the presser, when on the list.
export function presserDriver(people, currentUserId) {
  if (currentUserId == null) return '';
  const me = String(currentUserId);
  const onList = (Array.isArray(people) ? people : []).some(p => p && String(p.id) === me);
  return onList && drivesHere(people, me) ? me : '';
}

// The picker's entries, in order, with the escape always last.
//
// WHOEVER IS PRESSING COMES FIRST AND IS THE DEFAULT — Colby: "the picker pre
// fills with who ever opened the count in". Everyone else follows by name, so
// the common case is zero taps and the uncommon one is a normal select.
//
// #699, gate 665 D28 -- AND THE DRIVERS COME NEXT. "People switched on are
// offered first on the driver list. Everyone else stays reachable, and
// 'Someone else…' stays last." The presser leads only when they drive (or
// nobody at the branch is switched on, which is the old order exactly);
// otherwise they sit with everyone else, still marked as you.
export function driverChoices(people, currentUserId) {
  const rows = (Array.isArray(people) ? people : [])
    .filter(p => p && p.id != null)
    .map(p => ({ id: String(p.id), name: p.full_name || p.name || 'Unnamed', drives: isDriver(p) }));

  const me = currentUserId == null ? null : String(currentUserId);
  const byName = (a, b) => a.name.localeCompare(b.name);
  const leads = me && presserDriver(people, me) === me;
  const mine = leads ? rows.filter(r => r.id === me) : [];
  const rest = rows.filter(r => !(leads && r.id === me));
  const drivers = rest.filter(r => r.drives).sort(byName);
  const others = rest.filter(r => !r.drives).sort(byName);

  return [
    ...mine.map(r => ({ ...r, isMe: true })),
    ...[...drivers, ...others].map(r => ({ ...r, isMe: r.id === me })),
    { id: DRIVER_SOMEONE_ELSE, name: 'Someone else…', isMe: false, isEscape: true },
  ];
}

// WHAT GETS WRITTEN, and it mirrors the column constraint rather than trusting
// the caller. Returns at most one of the two, never both.
export function driverPayload(pick, typedName) {
  const typed = typeof typedName === 'string' ? typedName.trim() : '';
  if (pick === DRIVER_SOMEONE_ELSE) {
    return { driverId: null, driverName: typed || null };
  }
  if (pick == null || pick === '') {
    return { driverId: null, driverName: null };
  }
  // A REAL ACCOUNT WINS AND THE TYPED NAME IS DROPPED. Somebody who opens the
  // escape, types a name, then picks a person again must not leave both set --
  // the database would refuse the whole load with a constraint violation at
  // the moment somebody presses Send.
  return { driverId: String(pick), driverName: null };
}

// LOADING A TRUCK. Runs as many times as there are trucks.
//
// `fromBranchId` is not decoration: the second load leaves Austin rather than
// Houston, and the database refuses a quantity larger than what is standing at
// whichever place it is told.
//
// THE DRIVER IS NEVER ASKED HOW A LINE WAS FILLED — D64. `sourced` defaults
// from what was approved, and whether frames were cut this week or pulled off a
// rack is a fabrication fact answered by whoever staged the goods. The driver's
// screen asks one thing: how many.
export async function loadTransfer(transfer, fromBranchId, lines, {
  toBranchId = null, driverName = null, truck = null, notes = null,
  driverId = null, toPlace = null, branchNames = {},
} = {}) {
  const payload = (Array.isArray(lines) ? lines : [])
    .filter(l => Number(l.qty) > 0)
    .map(l => ({
      item_id: l.item_id,
      qty: Number(l.qty),
      unit_cost: l.unit_cost == null ? null : Number(l.unit_cost),
      sourced: l.sourced || null,
    }));
  if (payload.length === 0) return { error: new Error('nothing to load') };

  // A LOAD ENDS AT A BRANCH OR AT A PLACE ON THE ROAD, NEVER BOTH AND NEVER
  // NEITHER-BUT-BOTH-NAMED -- #553 D10, and `transfer_load_to_one_place`
  // enforces it. Caught here rather than at the database so somebody reads a
  // sentence instead of a constraint name.
  const place = typeof toPlace === 'string' && toPlace.trim() ? toPlace.trim() : null;
  if (place && toBranchId != null) {
    return { error: new Error('a load is left at a branch or at a spot on the road, not both') };
  }

  const { data: ref, error } = await supabase.rpc('load_branch_transfer', {
    p_transfer_id: transfer.id,
    p_from_branch: fromBranchId,
    p_lines: payload,
    p_to_branch: toBranchId,
    // D12. `driver_id` is an account and `driver_name` is a typed string for
    // somebody without one; transfer_load_driver_one_way refuses both at once,
    // so the id wins and the name is dropped rather than sent alongside it.
    p_driver_name: driverId ? null : driverName,
    p_truck: truck,
    p_notes: notes,
    p_to_place: place,
    p_driver_id: driverId,
  });
  if (error) return { error };

  // ── #648, Session 79.22b — A TRUCK HAS LEFT FOR US ─────────────────────────
  //
  // RULED BY COLBY at 79.22a's close: "i like the departure bell". Nothing was
  // sent when a truck left, while Notification preferences offered a switch
  // called "Transfer shipped" - a promise with nothing behind it, because the
  // only sender was shipTransfer(), which no screen calls since loading
  // replaced shipping.
  //
  // THE RECEIVING BRANCH'S APPROVERS, AND NOT THE PERSON WHO ASKED. Gate D30
  // took the requester's shipped bell away on purpose - My requests carries
  // the play-by-play - and acceptance line K8's first clause checks it stays
  // gone, so the asker is removed even when they are also an approver.
  //
  // ONLY THE LEG BOUND FOR THE RECEIVING BRANCH. A load dropped at a hub or a
  // spot on the road is not coming to them yet, and a hub journey would
  // otherwise page the same managers twice; the leg that sets off for them is
  // the one they prepare for. A request for the branch's own shelf never
  // travels.
  const boundForReceiver = !place
    && (toBranchId == null || String(toBranchId) === String(transfer?.to_branch_id));
  if (boundForReceiver && transfer?.to_branch_id && !isOwnShelf(transfer)) {
    const receivers = await askingApproverIds(transfer);
    const audience = [...new Set((receivers || []).filter(Boolean))]
      .filter(id => String(id) !== String(transfer.requested_by));
    if (audience.length) {
      const going = payload.reduce((n, l) => n + l.qty, 0);
      const from = branchNames?.[String(fromBranchId)] || 'the sending branch';
      await sendNotification(
        audience,
        'Material on its way',
        `${ref || 'A load'} left ${from} with ${going} — ${transferLabel(transfer, branchNames)}`,
        'transfer_shipped',
        'inventory',
        'transfers',
        transfer.id,
      );
    }
  }

  return { ok: true, loadRef: ref };
}

// #694, gate 665 D21, Session 79.22b.4.6.1 -- TAKING A SEND BACK.
//
// One database call, all or none: undo_load() marks the truck undone - never
// deleted - puts the stock back on the sending branch's shelf as reversal rows
// naming each send row (SAP's reversal document), recounts what was sent, and
// sends the transfer back to waiting to load when no other truck is live on it.
// It refuses anybody but the person who pressed Send, a truck already undone,
// and one collected, handed over, marked as here or counted in since - in
// sentences written for a person. No time limit (Colby's R2 at 79.22b.4.6).
//
// SENDS NOTHING (gate 665 B5): the departure bell the send rang stays (R2), and
// the transfer's history shows the send and its Undo.
//
// `undo` is sendUndo()'s own object. An answer that does not name the truck it
// was asked about is not a confirmed write.
export async function undoSend(undo) {
  const ref = typeof undo?.loadRef === 'string' ? undo.loadRef.trim() : '';
  if (undo?.transferId == null || !ref) {
    return { error: new Error("That can't be undone from here. Tell a manager so the count can be corrected.") };
  }
  const { data, error } = await supabase.rpc('undo_load', { p_transfer_id: undo.transferId, p_load_ref: ref });
  if (error) return { error };
  if (!data || data.load_ref !== ref || !Array.isArray(data.lines)) {
    return { error: new Error('That did not go through. Nothing was undone — try again.') };
  }
  return {
    loadRef: data.load_ref,
    lines: data.lines.map(l => ({ itemId: l.item_id, qty: Number(l.qty) })),
    waitingToLoad: !!data.waiting_to_load,
    status: data.status ?? null,
  };
}

// A LOAD ARRIVING, AND BEING COUNTED.
//
// `counts` is [{ item_id, counted }] and goes through countsPayload() — see its
// note. Passing nothing means nothing was wrong, which is D63's default of
// silence and is what keeps the ordinary Wednesday at two taps.
//
// D68, COLBY'S RULING, replacing D60: every arrival tells the requester AND the
// approver, and the approver also gets an email.
//
// His case is the argument. Four hundred frames cover three orders at 200, 150
// and 50; seventy-five arrive; nobody is told; the fifty-frame order misses its
// date for no reason. AN ARRIVAL IS NOT AN INTERRUPTION, IT RELEASES WORK.
//
// The email goes to the approver rather than the driver, which is #514's D29
// holding: 83% of non-desk workers have no reliable email, so mailing the
// person standing at the rack is unreliable by construction.
// ── THE TRUCK IS HERE — ONE TAP, AND NOTHING ELSE — #553 D17 ────────────────
//
// Separate from counting because they are separate events, and Colby's own
// business fact is the reason: "on transfer days the driver gets back to dallas
// from austin late in the evening. the truck is always unloaded the following
// morning."
//
// NO COUNTS, NO STOCK, NO SHEET. A driver standing in a dark yard presses one
// thing. It is IDEMPOTENT on purpose — pressing it twice is not an error and
// must not be reported as one, because being told off by an application for
// pressing a button twice is worse than the duplicate.
//
// IT TELLS NOBODY, DELIBERATELY. The arrival notification belongs to the COUNT,
// where a real quantity exists to put in it — D68's message names what arrived
// and what is still owed, and neither number is known yet. A bell saying "a
// truck is somewhere" is how a bell stops being read.
export async function markArrived(load) {
  if (!load?.id) return { error: new Error('no load') };
  const { data, error } = await supabase.rpc('mark_load_arrived', { p_load_id: load.id });
  if (error) return { error };
  // The function returns what it changed and the screen renders it back.
  // `error === null` is not proof a write happened — PostgREST reports zero
  // rows changed as success, which is how 79.1's packing-slip control said
  // "Added just now" while writing nothing.
  return { ok: true, result: data || {}, alreadyArrived: !!(data || {}).already_arrived };
}

// ── COUNTING IT IN — THIS IS WHEN THE STOCK LANDS — #553 D17 ────────────────
//
// RENAMED FROM `arriveLoad` at Session 79.15f, and the rename is the point
// rather than tidying: a function called "arrive" that also counted is exactly
// the conflation this session removes. A truck back Tuesday night and counted
// Wednesday morning should read WEDNESDAY, because that is when somebody
// actually looked in the boxes.
//
// It works whether or not anybody tapped arrived — the database fills the
// arrival time in if the tap never happened, so a forgotten press cannot block
// the morning crew.
export async function countInLoad(load, counts, {
  transfer = null, branchNames = {}, note = null,
} = {}) {
  if (!load?.id) return { error: new Error('no load') };
  const t = transfer || load.branch_transfers || null;

  // #634, Session 79.22b -- THE COST THAT TRAVELLED LANDS IN THE AVERAGE. Only
  // at the destination, because a leg ending at a hub books nothing in. The
  // shelf is read fresh HERE, immediately before the count, the way the
  // purchasing receive does it, so the blend is not worked out from a number
  // the screen loaded an hour ago. A failed read stops the count before
  // anything is written: counting in with the average silently skipped is #634
  // itself, and a retry costs one tap.
  let avgCosts = null;
  const dest = t?.to_branch_id ?? null;
  if (dest != null && String(load.to_branch_id) === String(dest)) {
    const loadItems = load.branch_transfer_load_items || load.items || [];
    const ids = [...new Set(loadItems.map(li => li?.item_id).filter(v => v != null))];
    if (ids.length) {
      const { data: stock, error: stockError } = await supabase
        .from('inventory_stock')
        .select('item_id, qty_on_hand, avg_unit_cost')
        .eq('branch_id', dest)
        .in('item_id', ids);
      if (stockError) return { error: stockError };
      avgCosts = averagesOnArrival({ loadItems, counts, stock: stock || [] });
    }
  }

  const { data, error } = await supabase.rpc('count_in_load', {
    p_load_id: load.id,
    p_counts: countsPayload(counts),
    p_note: note,
    p_avg_costs: avgCosts,
  });
  if (error) return { error };

  const result = data || {};

  // #682, 79.22b.6c.3 -- A COUNT ABOVE THE TRUCK ASKED THE SENDER (682 D7), and
  // the sender is told outside Transfers too: transfer_extra_counted to the
  // truck's loader and the sending branch's leads (gate B5). Before the early
  // return below, though a stop's count never asks.
  if (Array.isArray(result.asked) && result.asked.length) {
    const itemNames = Object.fromEntries((load.branch_transfer_load_items || [])
      .filter(li => li?.inventory_items?.name).map(li => [String(li.item_id), li.inventory_items.name]));
    await notifyTruckQuestions({ asked: result.asked, branchNames, itemNames });
  }

  // Nothing landed on a shelf, so nobody is waiting on this. A load reaching a
  // staging point is a movement, not a delivery, and telling three people it
  // arrived is how a bell stops being read. This is the Austin leg.
  if (!result.at_destination) return { ok: true, result };

  // WHAT ARRIVED AND WHAT IS STILL OWED ARE READ BACK, NEVER ASSUMED. The
  // rollup trigger has just moved qty_received on every line, so the transfer
  // object this function was handed is already stale -- and both numbers go
  // into a message a person acts on. 79.9's whole headline was a list that was
  // typed instead of read.
  let lines = [];
  let outstanding = 0;
  const transferId = t?.id ?? load.transfer_id;
  const { data: fresh } = await supabase
    .from('branch_transfer_items')
    .select('*, inventory_items(id, name, uom)')
    .eq('transfer_id', transferId);
  for (const li of (fresh || [])) {
    const p = lineProgress(li);
    outstanding += p.outstanding;
  }

  // The lines on THIS load, at what was BOOKED IN -- which is what the reader
  // wants to know arrived today, not the running total. #682, 79.22b.6c.2b: a
  // count above the truck books the truck's number and asks the sender about
  // the rest, so "arrived with 4" when 2 went on the shelf would tell the asking
  // branch about stock nobody has (the gate's B5: "with the number booked").
  const counted = countsPayload(counts) || {};
  lines = (load.branch_transfer_load_items || []).map(li => ({
    name: li.inventory_items?.name || 'Item',
    qty: bookedOnArrival(li, counted),
    uom: li.inventory_items?.uom || null,
  }));

  // #599, Session 79.21b -- AN ENDED TRANSFER OWES NOTHING. `transfer_complete`
  // only says whether THIS count finished the transfer. One that had already
  // ended as "nothing more coming" -- the asking branch stopping the rest (371
  // D33) or the sender's own close -- stays ended, so the balance read above is
  // what was never going to come, not what is owed. Read fresh, like the lines:
  // the row the page handed over may predate the ending.
  const { data: nowRow } = await supabase
    .from('branch_transfers').select('status').eq('id', transferId).maybeSingle();
  const ended = !result.transfer_complete
    && ['closed_unfilled', 'closed_short'].includes(nowRow?.status);

  // #649, Session 79.22b -- the asker's DEPARTMENT manager too, who approved
  // stage one: this passed a literal null, which reaches the branch manager
  // alone. The load's own branch is the fallback when no transfer is in hand.
  const approvers = t?.to_branch_id
    ? await askingApproverIds(t)
    : await getRequestApproverIds(load.to_branch_id, null);
  const audience = [...new Set([t?.requested_by, ...approvers].filter(Boolean))];

  const arrived = lines.reduce((n, l) => n + (Number(l.qty) || 0), 0);
  const label = t ? transferLabel(t, branchNames) : (load.load_ref || 'A transfer');
  const body = result.transfer_complete
    ? `${label} — the last of it has arrived`
    : ended
      ? `${label} — ${load.load_ref || 'a load'} arrived with ${arrived}. Nothing more is coming.`
      : `${label} — ${load.load_ref || 'a load'} arrived with ${arrived}, ${outstanding} still owed`;

  await sendNotification(
    audience,
    'Material has arrived',
    body,
    'transfer_load_arrived',
    'inventory',
    'transfers',
    transferId,
  );

  // The approver by email as well -- D68. notifyTransferLoadArrived() catches
  // its own failures: the stock is booked in and the bells have gone, and a
  // mail problem is not a reason to tell the person at the rack that their
  // count did not save.
  await notifyTransferLoadArrived({
    toBranchId: t?.to_branch_id ?? load.to_branch_id,
    // #649 -- the email reaches the same approvers the bell does.
    departmentId: t?.requested_by_department_id ?? null,
    label,
    loadRef: load.load_ref || loadRef(t?.transfer_number, load.load_seq),
    // #726 -- a leg that began on the road names the road place.
    fromBranchName: legEnds(load, branchNames).from || 'another branch',
    toBranchName: branchNames[String(load.to_branch_id)] || 'here',
    lines,
    outstanding,
    note,
    transferId,
    ended,
  });

  return { ok: true, result };
}

// ── COLLECTING FROM A HANDOVER — ONE PRESS, TWO RECORDS ─────────────────────
//
// #529, Session 79.10, after Colby: "dropping off frames at the hub in austin
// is the standard... a handoff happens 99.5% of the time." The build treated it
// as the exception and left the collect tab with NO ACTION BEHIND IT.
//
// A collection is two facts and the driver should never meet both: the inbound
// legs END here, and an outbound leg STARTS here with whatever fits. Making somebody press twice
// would put the normal path at four taps while the rare straight-through run
// costs two.
//
// ** NEITHER HALF MOVES STOCK, WHICH IS WHY ONE PERSON MAY DO BOTH. ** Arriving
// at a hub books nothing in -- the goods are not at their destination -- and
// loading FROM a hub takes nothing off a shelf, because it left the origin's
// shelf on the first leg. So the branch-scoped rule on stock rows never fires,
// and a Dallas driver standing at Austin can record the whole handover. That
// was checked against the migration rather than assumed.
//
// COUNTING IS THE EXCEPTION TAP - Colby: "usually no count, but the driver
// can." Passing no counts means what was dropped is what was found, which is
// D63's default of silence and what keeps this at two taps.
//
// ** AND WHAT WAS FOUND IS NOT WHAT WAS TAKEN. ** Those are two numbers and the
// first build had one. See waitingAtHub().
//
// A SWAP AND A HUB DROP ARE THE SAME RECORD, which is his third ruling: "a
// handover at a place". A stretch of road and a yard differ only in where the
// material waited.
// ** ONE CALL NOW, NOT TWO — #553 D11, and it closes a locked-rule-7 gap. **
//
// This used to be a CLIENT-SIDE two-step: `arrive_transfer_load` once per
// inbound leg, then `load_branch_transfer` for the onward one. It was careful
// about it — neither half moves stock, and it reported which half stood — so it
// was recoverable rather than dangerous, and material never disappeared.
//
// It is still two writes for one physical event, and the gate's own write
// specification already required a single atomic call citing locked rule 7. So
// `hand_over_load()` REPLACES the chain rather than sitting beside it.
//
// THE DATABASE FUNCTION TAKES AN ARRAY BECAUSE A YARD CAN HOLD TWO TRUCKS. The
// gate named a single load and could not have expressed the two-truck case this
// helper already handled — corrected in the gate at B3.
//
// A HANDOVER AND A HUB DROP ARE THE SAME RECORD, which is Colby's ruling: "a
// handover at a place". A stretch of road and a yard differ only in where the
// material waited, and `at.place` versus `at.branchId` is the only difference
// in the call.
export async function handOverLoad(transfer, at, {
  loads = [], taking = [], counts = null, note = null,
  toUser = null, toName = null, driverId = null, driverName = null, truck = null,
  byName = null, branchNames = {},
} = {}) {
  if (!transfer?.id) return { error: new Error('no transfer') };
  const dest = transfer.to_branch_id;
  if (dest == null) return { error: new Error('cannot tell where this is going') };

  // The second argument is a branch id for a yard, or { place } for a spot on
  // the road. Exactly one, mirroring the column constraint.
  const atBranch = (at && typeof at === 'object') ? (at.branchId ?? null) : (at ?? null);
  const atPlace = (at && typeof at === 'object' && at.place) ? String(at.place).trim() : null;
  if ((atBranch == null) === (!atPlace)) {
    return { error: new Error('a handover happens at a branch or at a place, not both and not neither') };
  }

  const all = Array.isArray(loads) ? loads : [];
  const inbound = all.filter(l =>
    l?.status === 'in_transit' &&
    (atBranch != null
      ? String(l.to_branch_id) === String(atBranch)
      : String(l.to_place || '') === atPlace) &&
    (l.transfer_id == null || String(l.transfer_id) === String(transfer.id)));

  // WHAT WAS FOUND IS NOT WHAT WAS TAKEN. Two numbers: `counts` is what was
  // standing there, `taking` is what went on the truck. Whatever is left over
  // stays standing and keeps appearing on To collect until somebody fetches it.
  const lines = (Array.isArray(taking) ? taking : [])
    .map(t => ({
      item_id: t.item_id,
      qty: Number(t.qty),
      unit_cost: t.unit_cost ?? null,
      sourced: t.sourced ?? null,
    }))
    .filter(l => Number.isFinite(l.qty) && l.qty > 0);

  const shape = handoverShape({ inbound: inbound.length, taking: lines.length });
  if (!shape.ok) return { error: new Error(shape.reason) };

  // A count is only passed when ONE leg can own it. With two trucks at one
  // yard, "190 were here" has no honest home, and splitting it across them
  // would be arithmetic nobody asked for. The screen offers the count option on
  // the same condition.
  const { data, error } = await supabase.rpc('hand_over_load', {
    p_load_ids: inbound.map(l => l.id),
    p_counts: countsPayload(inbound.length === 1 ? counts : null),
    p_at_branch: atBranch,
    p_place: atPlace,
    p_to_user: toUser,
    p_to_name: toUser ? null : (toName || null),
    p_taking: lines.length ? lines : null,
    p_to_branch: dest,
    p_driver_id: driverId,
    p_driver_name: driverId ? null : (driverName || null),
    p_truck: truck,
    p_note: inbound.length === 1 ? note : null,
    // Named because a leftover pickup has no leg to read the transfer from, and
    // the database derives the predecessor so the chain survives a second trip.
    p_transfer_id: transfer.id,
  });

  // ONE CALL MEANS ONE ANSWER. The old chain had to explain which half stood;
  // this either happened or it did not, so the message is just the message.
  if (error) return { error };

  const result = data || {};
  const out = {
    ok: true,
    loadRef: result.load_ref || null,
    legNo: result.leg_no ?? null,
    arrived: result.legs_closed ?? inbound.length,
    collectedNothing: !!result.collected_nothing,
    taken: Number(result.taken) || 0,
    missing: Number(result.missing) || 0,
    extra: Number(result.extra) || 0,
    // #694, 79.22b.4.6.1b -- the record this collection wrote, which is what
    // its Undo names (undo_collection). Null from a body older than
    // 20260927_undo_collect_694, and then the card offers no Undo.
    handoverId: result.handover_id ?? null,
  };
  // #631 -- only when ONE leg owned the count, which is the only time a count
  // was passed at all (see above), so there is exactly one leg to name.
  // #682, Session 79.22b.6c.2b -- SHORT ONLY. hand_over_load() now refuses a
  // count above the truck it closes (682 D8), so a collection can no longer
  // record an extra and its "Handed over with extra" bell can never ring; it is
  // removed rather than left as a branch nothing reaches.
  if (out.missing > 0 && inbound.length === 1) {
    await tellHandoverDiscrepancy(transfer, inbound[0], out, {
      atBranch, atPlace, note, toUser, toName, byName, branchNames,
    });
  }
  return out;
}

// #694, gate 665 D21, Session 79.22b.4.6.1b -- A COLLECTION TAKEN BACK.
//
// undo_collection() finds it by the record hand_over_load() wrote and
// returned, puts every truck the collection closed back exactly as its record
// says it was, and takes the onward truck off the road, marked undone. It
// refuses anybody but the collector, and anything that has happened since, in
// the card's words. It SENDS NOTHING (gate B5): a short-or-over bell the
// collection rang stays, as every press's message does (R2).
export async function undoCollect(undo) {
  const id = undo?.handoverId == null ? NaN : Number(undo.handoverId);
  if (!Number.isFinite(id)) {
    return { error: new Error("That can't be undone from here. Tell a manager so the count can be corrected.") };
  }
  const { data, error } = await supabase.rpc('undo_collection', { p_handover_id: id });
  if (error) return { error };
  if (!data || Number(data.handover_id) !== id || !Array.isArray(data.legs)) {
    return { error: new Error('That did not go through. Nothing was undone — try again.') };
  }
  return {
    handoverId: id,
    loadRef: data.load_ref || null,
    taken: Number(data.taken) || 0,
    legs: data.legs.map(l => ({ loadId: l.load_id, loadRef: l.load_ref || null })),
    atBranchId: data.at_branch_id ?? null,
    atPlace: data.at_place || null,
    status: data.status ?? null,
  };
}

// #694, gate 665 D21, Session 79.22b.4.6.2 -- A COUNT TAKEN BACK.
//
// undo_count_in() finds it by the record count_in_load() wrote and returned,
// takes the stock back off the shelf as reversal rows naming each count row,
// puts the branch's average cost back to exactly what the record says it was
// -- never worked out again -- and reopens a request the count had finished.
// It refuses anybody but the counter, and anything that has changed that
// item on that shelf, the truck or the request since, in the card's words. It
// SENDS NOTHING (gate B5): the arrival bell and email the count sent stay (R2).
export async function undoCountIn(undo) {
  const id = undo?.countInId == null ? NaN : Number(undo.countInId);
  if (!Number.isFinite(id)) {
    return { error: new Error("That can't be undone from here. Tell a manager so the count can be corrected.") };
  }
  const { data, error } = await supabase.rpc('undo_count_in', { p_count_in_id: id });
  if (error) return { error };
  if (!data || Number(data.count_in_id) !== id || !Array.isArray(data.lines)) {
    return { error: new Error('That did not go through. Nothing was undone — try again.') };
  }
  return {
    countInId: id,
    loadId: data.load_id ?? null,
    loadRef: data.load_ref || null,
    counted: Number(data.counted) || 0,
    lines: data.lines.map(l => ({ itemId: l.item_id, qty: Number(l.qty) })),
    atBranchId: data.at_branch_id ?? null,
    reopened: !!data.reopened,
    costRestored: !!data.cost_restored,
    status: data.status ?? null,
  };
}

// #631, Session 79.22b -- A HANDOVER COUNTED SHORT OR OVER TELLS BOTH ENDS.
//
// The take-on sheet tells the driver it "is recorded against both of you with
// the time and the place", and hand_over_load() does record it - the note, the
// time, the place, both people. What nobody got was a bell: two items could
// go missing at a yard between two named employees and no manager heard. The
// short close already solves the audience - both branches' managers - and
// this is the same event one step earlier, so it uses the same two lookups.
//
// THE VALUE IS GATE 553 B5's OWN, transfer_handed_over - "both branch
// managers if short", "No when short: a discrepancy with a name on it follows
// the closed_short precedent". 79.15f put it on both databases and nothing
// ever sent it; #631 was logged saying a new value was needed, and it was not.
// So no switch, like the short close. The time is the bell's own - it is sent
// in the same moment the handover is recorded.
async function tellHandoverDiscrepancy(transfer, leg, got, {
  atBranch = null, atPlace = null, note = null, toUser = null, toName = null, byName = null, branchNames = {},
} = {}) {
  const ids = [leg?.driver_id, toUser].filter(Boolean).map(String);
  let named = {};
  if (ids.length) {
    const { data } = await supabase.from('users').select('id, full_name').in('id', ids);
    named = Object.fromEntries((data || []).map(u => [String(u.id), u.full_name]));
  }
  // #728 -- one rule with the history's handover row.
  const giver = handoverGiver(leg, { nameOf: id => named[String(id)] || null, branchNames });
  const taker = toName || named[String(toUser)] || byName || 'the driver taking it on';
  const place = atPlace || branchNames[String(atBranch)] || 'the handover';
  const ref = leg?.load_ref || transferLabel(transfer, branchNames);
  // #682, 79.22b.6c.2b -- short only: a collection can no longer record an extra.
  const what = `${got.missing} short`;
  const why = String(note || '').trim();
  const [fromMgrs, toMgrs] = await Promise.all([
    getRequestApproverIds(transfer.from_branch_id, null),
    getRequestApproverIds(transfer.to_branch_id, null),
  ]);
  await sendNotification(
    [...new Set([...(fromMgrs || []), ...(toMgrs || [])].filter(Boolean))],
    'Handed over short',
    `${ref} at ${place}: ${what}. Handed over by ${giver}, taken on by ${taker}.${why ? ` “${why}”` : ''}`,
    'transfer_handed_over',
    'inventory',
    'transfers',
    transfer.id,
  );
}

// THE SECOND ENDING — D56. Nothing left the building, so there is no money to
// report, which is exactly why this is not the short close.
export async function closeTransferUnfilled(transfer, reason, { branchNames = {} } = {}) {
  if (!reason || !String(reason).trim()) {
    return { error: new Error('a reason is required') };
  }

  const { data: units, error } = await supabase.rpc('close_branch_transfer_unfilled', {
    p_transfer_id: transfer.id,
    p_reason: String(reason).trim(),
  });
  if (error) return { error };

  // THE AUDIENCE IS NARROWER THAN THE SHORT CLOSE'S, AND THAT IS THE WHOLE
  // POINT OF D56. The requester, because they are the one who stops waiting,
  // and the receiving branch's managers, who were expecting a delivery.
  //
  // NOT both branches' managers plus every admin, which is what
  // `transfer_closed_short` reaches. That value is the audit ping for LOST
  // MONEY and it carries no opt-out toggle, deliberately. Nothing left the
  // building here, so there is no money to report.
  //
  // THE GATE'S ACCEPTANCE LINE S6 IS THE TEST: "'Nothing more is coming'
  // closes the balance, reports no money lost, and DOES NOT PAGE ANYBODY WITH
  // A LOSS." The first draft of this function reached for
  // `transfer_closed_short` and would have done exactly that -- a loss report
  // in front of a manager for a truck that was never loaded, and every future
  // report counting losses silently wrong.
  //
  // `transfer_closed_unfilled` is added by
  // db/migrations/20260911_transfer_closed_unfilled_event.sql.
  // #649, Session 79.22b -- "the RECEIVING branch's approving managers", as
  // NOTIFICATION_VOCABULARY.md has always said: the asker's department manager
  // is one of them, and a literal null reached the branch manager alone.
  const receivers = await askingApproverIds(transfer);
  const audience = [...new Set([transfer.requested_by, ...receivers].filter(Boolean))];
  await sendNotification(
    audience,
    'Nothing more is coming',
    `${transferLabel(transfer, branchNames)} — ${units} not being sent. ${String(reason).trim()}`,
    'transfer_closed_unfilled',
    'inventory',
    'transfers',
    transfer.id,
  );

  return { ok: true, units };
}
